import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { colors } from "../components/Theme/WebTheme";
import {
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Modal,
  FormLabel,
  Button
} from '@mui/material';
import { useTranslation } from "react-i18next";
import OtherPerson from 'components/OtherPerson';
import {
  getBookingWorkflow,
  findWorkflowConfig,
  getVehicleSize,
} from 'common/src/other/bookingWorkflow';

export const calcEst = false;
export const showEst = true;
export const optionsRequired = true;

export const MAIN_COLOR = colors.DELIVERYPRIMARY;
export const SECONDORY_COLOR = colors.DELIVERYSECONDORY;

export const FONT_FAMILY =  '"Roboto", "Helvetica", "Arial", sans-serif';

export const formatAmount = (value, decimals = 2) => {
  const number = parseFloat(value);
  const safeNumber = isNaN(number) ? 0 : number;
  return safeNumber.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const formatCurrency = (value, settings = {}) => {
  const amount = formatAmount(value, 2);
  const symbol = settings && settings.symbol ? settings.symbol : "";
  return settings && settings.swipe_symbol ? `${amount} ${symbol}`.trim() : `${symbol} ${amount}`.trim();
};

const parseMoneyValue = (value) => {
  const amount = parseFloat(value);
  return isNaN(amount) ? null : amount;
};

const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const pendingPaymentAmount = () => ({
  amount: null,
  source: "pendingBidAcceptance",
  isBidAmount: false,
  pending: true
});

const resolvedPaymentAmount = (value, source, isBidAmount) => {
  const amount = parseMoneyValue(value);
  return amount === null ? pendingPaymentAmount() : {
    amount,
    source,
    isBidAmount,
    pending: false
  };
};

export const getFinalPaymentAmount = (booking) => {
  if (!booking) return pendingPaymentAmount();
  if (booking.acceptedOfferType === "fleet") {
    const offer = booking.acceptedFleetOffer || {};
    return resolvedPaymentAmount(firstPresent(offer.amount, offer.bidAmount), "acceptedFleetOffer", true);
  }
  if (booking.acceptedOfferType === "driver") {
    const offer = booking.acceptedDriverOffer || {};
    const selectedBid = booking.selectedBid || {};
    return resolvedPaymentAmount(firstPresent(offer.trip_cost, offer.amount, selectedBid.trip_cost, selectedBid.amount), "acceptedDriverOffer", true);
  }
  if (booking.acceptedFleetOffer) {
    const offer = booking.acceptedFleetOffer || {};
    return resolvedPaymentAmount(firstPresent(offer.amount, offer.bidAmount), "acceptedFleetOffer", true);
  }
  if (booking.selectedBid) {
    const selectedBidAmount = firstPresent(booking.selectedBid.trip_cost, booking.selectedBid.amount);
    if (selectedBidAmount !== undefined) {
      return resolvedPaymentAmount(selectedBidAmount, "acceptedDriverOffer", true);
    }
  }
  const status = (booking.status || "").toString().toUpperCase();
  const hasAssignedDriverBid = booking.deliveryWithBid === true && !!booking.driver && ["ACCEPTED", "ARRIVED", "STARTED", "REACHED", "PENDING", "PAID", "COMPLETE", "COMPLETED"].indexOf(status) !== -1;
  if (hasAssignedDriverBid) {
    return resolvedPaymentAmount(booking.trip_cost, "acceptedDriverOffer", true);
  }
  if (booking.deliveryWithBid === true) return pendingPaymentAmount();
  return resolvedPaymentAmount(booking.trip_cost, "legacyTripCost", false);
};

export const getFinalBidAmount = (booking) => {
  const paymentAmount = getFinalPaymentAmount(booking);
  return paymentAmount.pending ? null : paymentAmount.amount;
};

const getConfiguredCommissionType = (booking) => (
  booking && firstPresent(
    booking.commissionType,
    booking.commission_type,
    booking.convenience_fee_type,
    (firstPresent(booking.commissionAmount, booking.commission_amount, booking.convenience_fees, booking.fleetCommission) !== undefined ? "flat" : "")
  )
);

const getConfiguredCommissionRate = (booking) => (
  booking && firstPresent(
    booking.commissionRate,
    booking.commission_rate,
    booking.convenience_fee_rate,
    booking.convenienceFeeRate
  )
);

const getConfiguredCommissionAmount = (booking) => (
  booking && firstPresent(
    booking.commissionAmount,
    booking.commission_amount,
    booking.convenience_fees,
    booking.fleetCommission,
    booking.driverAcceptanceFeeBreakdown && booking.driverAcceptanceFeeBreakdown.convenience_fees,
    booking.acceptedFleetOffer && booking.acceptedFleetOffer.commissionAmount
  )
);

export const getCommissionPreview = (booking) => {
  const bidAmount = parseMoneyValue(getFinalBidAmount(booking));
  if (bidAmount === null) {
    return { status: "pending", type: "", rate: null, commissionAmount: null, netAmount: null };
  }

  const commissionType = getConfiguredCommissionType(booking);
  const commissionRate = parseMoneyValue(getConfiguredCommissionRate(booking));
  const explicitAmount = parseMoneyValue(getConfiguredCommissionAmount(booking));

  if (commissionType === "percentage" && commissionRate !== null) {
    const commissionAmount = bidAmount * commissionRate / 100;
    return { status: "configured", type: "percentage", rate: commissionRate, commissionAmount, netAmount: Math.max(bidAmount - commissionAmount, 0) };
  }

  if ((commissionType === "flat" || commissionType === "flat_rate") && commissionRate !== null) {
    return { status: "configured", type: "flat", rate: commissionRate, commissionAmount: commissionRate, netAmount: Math.max(bidAmount - commissionRate, 0) };
  }

  if (explicitAmount !== null) {
    return { status: "configured", type: commissionType || "flat", rate: explicitAmount, commissionAmount: explicitAmount, netAmount: Math.max(bidAmount - explicitAmount, 0) };
  }

  return { status: "not_configured", type: "", rate: null, commissionAmount: null, netAmount: null };
};

export const getEarningsPreview = (booking) => {
  const bidAmount = parseMoneyValue(getFinalBidAmount(booking));
  const commission = getCommissionPreview(booking);
  if (bidAmount === null) {
    return { status: "pending", bidAmount: null, payeeType: "", payeeName: "", netAmount: null, commission };
  }

  const isFleet = booking && booking.acceptedOfferType === "fleet";
  const isDriver = booking && booking.acceptedOfferType === "driver";
  const driverOffer = booking && (booking.acceptedDriverOffer || booking.selectedBid || booking);
  const fleetOffer = booking && (booking.acceptedFleetOffer || booking);
  const driverLinkedToFleet = !!(driverOffer && driverOffer.fleetadmin);
  const payeeName = isFleet
    ? getFleetAdminDisplayName(fleetOffer)
    : ((driverOffer && (driverOffer.driver_name || driverOffer.driverName)) || (booking && booking.driver_name) || "");

  if (isDriver && driverLinkedToFleet) {
    return { status: "handled_by_fleet_admin", bidAmount, payeeType: "driver", payeeName, netAmount: null, commission };
  }

  return {
    status: commission.status === "configured" ? "configured" : commission.status,
    bidAmount,
    payeeType: isFleet ? "fleet" : (isDriver ? "driver" : "legacy"),
    payeeName,
    netAmount: commission.netAmount,
    commission
  };
};

const hasActiveOffer = (offer) => offer && offer.status !== "cancelled";

const hasOwnFleetOffer = (booking, profile) => {
  const uid = profile && (profile.uid || profile.id);
  return !!(booking && uid && booking.fleetOffers && hasActiveOffer(booking.fleetOffers[uid]));
};

export const getBookingStatusLabel = (booking, role, t, profile = {}) => {
  if (!booking) return "";
  const status = (booking.status || "").toString().toUpperCase();
  const label = (key, fallback) => labelText(t, key, fallback);
  if (status === "CANCELLED") return label("cancelled", "Cancelled");
  if (status === "COMPLETE" || status === "COMPLETED" || status === "PAID") return label("completed", "Completed");
  if (status === "STARTED") return label("in_progress_started", "In Progress / Started");
  if (status === "ARRIVED") return label("driver_arrived", "Driver Arrived");
  if (status === "REACHED") return label("reached", "Reached");
  if (status === "AWAITING_DRIVER_ASSIGNMENT") {
    if (role === "fleetadmin") return label("won_bid_awaiting_driver_assignment", "Won Bid / Awaiting Driver Assignment");
    if (role === "admin") return label("fleet_bid_accepted_awaiting_driver_assignment", "Fleet Bid Accepted / Awaiting Driver Assignment");
    return label("waiting_for_driver_assignment", "Waiting for Driver Assignment");
  }
  if (status === "ACCEPTED") {
    if (role === "driver" && booking.driver) return label("assigned_booking", "Assigned Booking");
    if (role === "customer") return booking.driver ? label("driver_assigned", "Driver Assigned") : label("accepted", "Accepted");
    if (role === "fleetadmin") return booking.driver ? label("driver_assigned_accepted", "Driver Assigned / Accepted") : label("accepted", "Accepted");
    if (role === "admin") return booking.driver ? label("driver_assigned_accepted", "Driver Assigned / Accepted") : label("accepted", "Accepted");
    return label("accepted", "Accepted");
  }
  if (status === "NEW" && booking.deliveryWithBid === true) {
    if (role === "fleetadmin") return hasOwnFleetOffer(booking, profile) ? label("bid_submitted", "Bid Submitted") : label("open_for_bid", "Open for Bid");
    if (role === "admin") return label("open_for_bid", "Open for Bid");
    if (role === "driver") return label("open_for_bid", "Open for Bid");
    return label("waiting_for_bids", "Waiting for Bids");
  }
  if (status === "PAYMENT_PENDING") return label("payment_pending", "Payment Pending");
  if (status === "PENDING") return label("pending", "Pending");
  return label(status, status);
};

export const getBookingStatusColor = (booking) => {
  const status = (booking && booking.status ? booking.status : "").toString().toUpperCase();
  if (status === "CANCELLED") return colors.RED;
  if (status === "COMPLETE" || status === "COMPLETED" || status === "PAID") return colors.GREEN;
  if (status === "ACCEPTED" || status === "ARRIVED" || status === "STARTED" || status === "REACHED") return colors.INDICATOR_BLUE || colors.DELIVERYPRIMARY;
  return colors.YELLOW;
};

export const getAcceptedOfferSummary = (booking, settings = {}, t) => {
  if (!booking || !booking.acceptedOfferType) return "";
  const amount = getFinalBidAmount(booking);
  const amountText = amount !== null && amount !== undefined && amount !== "" ? formatCurrency(amount, settings) : "";
  if (booking.acceptedOfferType === "fleet") {
    const company = getFleetAdminDisplayName(booking.acceptedFleetOffer || booking);
    return [labelText(t, "accepted_offer_transport_company", "Accepted Offer: Transport Company"), company, amountText].filter(Boolean).join(" - ");
  }
  const offer = booking.acceptedDriverOffer || booking.selectedBid || booking;
  const driverName = offer.driver_name || offer.driverName || booking.driver_name || "";
  return [labelText(t, "accepted_offer_driver", "Accepted Offer: Driver"), driverName, amountText].filter(Boolean).join(" - ");
};

export const displayLabels = {
  serviceType: "Service Type",
  logistics_shipment: "Logistics Shipment",
  pickup_delivery: "Pickup & Delivery",
  pickup_delivery_information_form: "Pickup & Delivery Information",
  fleetadmin: "Fleet Admin / Logistics Company",
  fleetadmins: "Fleet Admins / Logistics Companies",
  fleetadmin_name: "Fleet Admin / Logistics Company Name",
  fleetadmin_id: "Fleet Admin / Logistics Company ID",
  fleet_admin_fee: "Fleet Admin Fee",
  fleet_admin_fee_required: "Fleet Admin Fee Required",
  fleet_admin_comission: "Fleet Admin Commission",
  fleetadmin_earning_reports: "Fleet Admin Earning Reports",
  drivers_threshold: "Drivers Threshold",
  driver_threshold_type: "Threshold Type",
  driver_threshold_value: "Threshold Value",
  driver_minimum_flat_rate: "Minimum Flat Rate",
  container_shipping_info: "Container / Shipping Info",
  container_yards_title: "Container Yard",
  container_yard_name: "Container Yard Name",
  container_yard_location: "Container Yard Location",
  available_slots: "Estimated Available Slots",
  total_slots: "Total Slots",
  pickup_container_yard: "Pickup Container Yard",
  empty_return_container_yard: "Empty Return Container Yard",
  containerNumber: "Container Number",
  additionalContainerNumbers: "Additional Container Numbers",
  add_additional_container_number: "Add Additional Container Number",
  additional_container_numbers: "Additional Container Numbers",
  bookingNumber: "Booking Number",
  blNumber: "BL Number",
  shippingLine: "Shipping Line",
  containerType: "Container Type",
  containerSize: "Container Size",
  grossWeight: "Gross Weight",
  pickupPort: "Pickup Port",
  deliveryWarehouse: "Delivery Address",
  pickupContainerYardName: "Pickup Container Yard",
  emptyReturnContainerYardName: "Empty Return Container Yard",
  yardGateStatus: "Container Yard / Empty Return Container Yard Status",
  yardGateRejectionReason: "Reason of Rejection",
  freeTimeExpiry: "Free Time Expiry",
  freeTimeExpiery: "Free Time Expiry",
  freeTimeExpiryDate: "Free Time Expiry Date",
  lastFreeDay: "Last Free Day",
  detentionStartDate: "Detention Start Date",
  detentionDays: "Detention Days",
  detentionPenalty: "Estimated Detention Penalty",
  detentionRemarks: "Detention Remarks",
  detention_status: "Detention Status",
  days_remaining: "Days Remaining",
  overdue_days: "Overdue Days",
  demurrageDeadline: "Demurrage Deadline",
  detentionDeadline: "Detention Deadline",
  dateConfirmationOk: "Date Confirmation",
  date_confirmation_ok_button: "OK",
  date_confirmation_confirmed: "Confirmed",
  date_confirmation_help: "Client confirms Free Time Expiry, Detention Deadline, and Demurrage Deadline.",
  gps_arrival_proof: "GPS Arrival Proof",
  pickup_photo_proof: "Pickup Photo Proof",
  delivery_photo_proof: "Delivery Photo Proof",
  container_yard_photo_proof: "Container Yard / Empty Return Container Yard Photo Proof",
  container_yard_video_proof: "Container Yard / Empty Return Container Yard Video Proof",
  time_date_stamp: "Time / Date Stamp",
  latitude: "Latitude",
  longitude: "Longitude",
  terminal_geofence_proof: "Terminal Geofence Proof",
  terminal_distance_km: "Terminal Distance (km)",
  proof_hash: "Proof Hash",
  previous_proof_hash: "Previous Proof Hash",
  photo_stamp: "Photo Stamp",
  empty_container_return: "Empty Container Return",
  empty_container_return_details: "Empty Container Return Details",
  garahex_empty_container_return_proof: "Garahex Empty Container Return Proof",
  print_ecr_proof: "Print ECR Proof",
  generated_date_time: "Generated Date/Time",
  proof_summary_footer: "This proof summary was generated from Garahex system records.",
  booking_parties: "Booking / Parties",
  container_details: "Container Details",
  return_status: "Return Status",
  gps_geofence_proof: "GPS / Geofence Proof",
  detention_free_time: "Detention / Free Time",
  proof_attachments: "Proof Attachments",
  container_yard_address: "Container Yard Address",
  driver_latitude: "Driver Latitude",
  driver_longitude: "Driver Longitude",
  container_yard_status: "Container Yard Status",
  container_yard_arrival_datetime: "Container Yard Arrival Date and Time",
  gps_arrival_picture: "GPS Picture of Arrival in Container Yard",
  gps_arrival_mobile_camera_note: "GPS arrival pictures must be captured only from the mobile app camera at the current location. Pictures from a computer upload or mobile gallery are not accepted.",
  container_yard_rejection_reason: "Container Yard Reason of Rejection",
  container_yard_acceptance_date: "Container Yard Date of Acceptance",
  container_yard_rejected_date: "Container Yard Date of Rejected",
  gps_arrival_picture_rejected: "Geotag GPS Picture of Arrival in Container Yard - Rejected",
  gps_arrival_picture_accepted: "Geotag GPS Picture of Arrival in Container Yard - Accepted",
  empty_container_return_web_required: "Please complete Container Yard and arrival date/time.",
  rejected_and_send: "Rejected and Send",
  accepted_and_send: "Accepted and Send",
  empty_container_return_locked: "Empty Container Return accepted and sent.",
  empty_container_return_sent: "Empty Container Return saved and email sent.",
  empty_container_return_saved_email_failed: "Empty Container Return saved, but email sending failed. Deploy or check the Cloud Function and SMTP settings.",
  empty_container_return_rejection_required: "Please enter the reason of rejection.",
  no_records_found: "No Records To Display",
  email_sent: "Email Sent",
  email_not_sent: "Email Not Sent",
  admin_chat_proof: "Chat Proof",
  conversation_proof: "Conversation Proof",
  additional_fees_title: "Additional Fees",
  send_booking_receipt: "Send Booking Receipt",
  favorite_transpo: "Favorite Transpo",
  select_favorite_transpo: "Choose Favorite Transpo",
  search_driver: "Search Logistics Company",
  no_drivers_found: "No drivers found",
  no_logistics_company_found: "No logistics company found",
  clear_favorite_transpo: "Clear Favorite Transpo",
  favorite_transpo_selected: "Favorite Transpo Selected",
  use_current_location: "Use Current Location",
  location_not_supported: "Location is not supported on this device.",
  booking_information: "Primary Booking Information Form",
  primary_booking_information_form: "Primary Booking Information Form",
  itemDescription: "Item Description",
  cargoLength: "Length",
  cargoWidth: "Width",
  cargoHeight: "Height",
  cargoDimensions: "Cargo Dimensions (in cm)",
  numberOfPieces: "Number of Pieces",
  note: "Note",
  exactDeliveryAddress: "Exact Delivery Address",
  pickupAddress: "Pickup Address",
  deliveryAddress: "Delivery Address",
  receiverName: "Receiver Name",
  receiverContactNumber: "Receiver Contact Number",
  preferredPickupDate: "Preferred Pickup Date",
  preferredPickupTime: "Preferred Pickup Time",
  shipmentType: "Shipment Type",
  domesticShipment: "Domestic Shipment",
  importShipment: "Import Shipment",
  exportShipment: "Export Shipment",
  domestic_booking_information_form: "Domestic Booking Information Form",
  import_booking_information_form: "Import Booking Information Form",
  export_booking_information_form: "Export Booking Information Form",
  domesticBookingNumber: "Booking Number",
  domesticBookingDate: "Booking Date",
  domesticShipmentType: "Shipment Type",
  shipperName: "Shipper Name",
  exporterShipperName: "Exporter/Shipper Name",
  consigneeName: "Consignee Name",
  contactPerson: "Contact Person",
  contactNumber: "Contact Number",
  completeDeliveryAddress: "Complete Delivery Address",
  blAwbNumber: "BL/AWB Number",
  portOfLoading: "Port of Loading",
  vesselEtd: "Vessel ETD",
  cargoCutOffDate: "Cargo Cut-Off Date",
  user_permissions_title: "User Permissions",
  user_type: "User Type",
  user_type_name: "User Type Name",
  create_user_type: "Create User Type",
  save_permissions: "Save Permissions",
  permission_add: "Add",
  permission_edit: "Edit",
  permission_view: "View",
  permission_delete: "Delete",
  permission_all: "All",
  check_all: "Check All",
  uncheck_all: "Uncheck All",
  user_type_name_required: "Please enter a user type name.",
  user_type_exists: "User type already exists."
};

const humanizeLabelKey = (key) => String(key || "")
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/[_-]+/g, " ")
  .replace(/\s+/g, " ")
  .trim()
  .replace(/\b\w/g, (char) => char.toUpperCase())
  .replace(/\bId\b/g, "ID")
  .replace(/\bOtp\b/g, "OTP")
  .replace(/\bGps\b/g, "GPS")
  .replace(/\bBl\b/g, "BL")
  .replace(/\bUrl\b/g, "URL");

export const labelText = (t, key, fallback) => {
  const value = t ? t(key) : key;
  const humanizedKey = humanizeLabelKey(key);
  const missingTranslation = typeof value === 'string' && value.toLowerCase().indexOf('missing') !== -1 && value.indexOf(key) !== -1;
  return value && value !== key && value !== humanizedKey && !missingTranslation ? value : (fallback || displayLabels[key] || humanizedKey);
};

const escapePrintHtml = (value) => String(value === 0 || value ? value : "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const printableValue = (value, emptyValue = "N/A") => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || emptyValue;
  if (value === true) return "True";
  if (value === false) return "False";
  return value === 0 || value ? value : emptyValue;
};

const isPrintableImageUrl = (url = "") => (
  /^data:image\//i.test(url) || /\.(png|jpe?g|gif|webp|bmp)(\?|#|$)/i.test(url)
);

export const openEmptyContainerReturnProofPrint = ({
  title = "Garahex Empty Container Return Proof",
  sections = [],
  attachments = [],
  footer = "This proof summary was generated from Garahex system records.",
  emptyValue = "N/A",
  generatedLabel = "Generated Date/Time",
  attachmentsTitle = "Proof Attachments"
} = {}) => {
  if (typeof window === "undefined") return false;
  const printWindow = window.open("", "_blank", "width=980,height=720");
  if (!printWindow) return false;
  const generatedAt = new Date().toLocaleString();
  const renderRows = (rows = []) => rows.map((row) => `
    <tr>
      <th>${escapePrintHtml(row.label)}</th>
      <td>${escapePrintHtml(printableValue(row.value, emptyValue))}</td>
    </tr>
  `).join("");
  const renderSections = sections.map((section) => `
    <section>
      <h2>${escapePrintHtml(section.title)}</h2>
      <table>
        <tbody>${renderRows(section.rows)}</tbody>
      </table>
    </section>
  `).join("");
  const renderAttachments = attachments.map((item) => {
    const url = item && item.url ? item.url : "";
    const label = item && item.label ? item.label : "";
    if (!url) {
      return `
        <div class="attachment">
          <h3>${escapePrintHtml(label)}</h3>
          <p>${escapePrintHtml(emptyValue)}</p>
        </div>
      `;
    }
    const imageMarkup = isPrintableImageUrl(url)
      ? `<img src="${escapePrintHtml(url)}" alt="${escapePrintHtml(label)}" />`
      : "";
    return `
      <div class="attachment">
        <h3>${escapePrintHtml(label)}</h3>
        <a href="${escapePrintHtml(url)}" target="_blank" rel="noreferrer">${escapePrintHtml(url)}</a>
        ${imageMarkup}
      </div>
    `;
  }).join("");
  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapePrintHtml(title)}</title>
    <style>
      * { box-sizing: border-box; }
      body { background: #fff; color: #111; font-family: Arial, Helvetica, sans-serif; margin: 28px; line-height: 1.35; }
      h1 { font-size: 24px; margin: 0 0 6px; }
      h2 { font-size: 16px; margin: 22px 0 8px; border-bottom: 1px solid #333; padding-bottom: 4px; }
      h3 { font-size: 13px; margin: 0 0 6px; }
      .meta { color: #333; font-size: 12px; margin-bottom: 16px; }
      table { width: 100%; border-collapse: collapse; page-break-inside: avoid; }
      th, td { border: 1px solid #999; padding: 7px 8px; text-align: left; vertical-align: top; font-size: 12px; }
      th { width: 34%; background: #f2f2f2; font-weight: 700; }
      .attachments { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
      .attachment { border: 1px solid #999; padding: 10px; page-break-inside: avoid; overflow-wrap: anywhere; }
      .attachment img { display: block; max-width: 100%; max-height: 320px; margin-top: 8px; object-fit: contain; border: 1px solid #ddd; }
      a { color: #111; text-decoration: underline; }
      footer { border-top: 1px solid #333; margin-top: 24px; padding-top: 10px; font-size: 12px; }
      @media print {
        body { margin: 14mm; }
        section, .attachment { page-break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <h1>${escapePrintHtml(title)}</h1>
    <div class="meta">${escapePrintHtml(generatedLabel)}: ${escapePrintHtml(generatedAt)}</div>
    ${renderSections}
    <section>
      <h2>${escapePrintHtml(attachmentsTitle)}</h2>
      <div class="attachments">${renderAttachments}</div>
    </section>
    <footer>${escapePrintHtml(footer)}</footer>
    <script>
      window.onload = function() {
        window.focus();
        setTimeout(function() { window.print(); }, 250);
      };
    </script>
  </body>
</html>`;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
};

export const CONTAINER_YARD_REGION_OPTIONS = [
  "Metro Manila",
  "Luzon",
  "Visayas",
  "Mindanao"
];

const getContainerYardName = (yard) => {
  return yard ? (yard.name || yard.container_yard_name || yard.containerYardName || yard.title || "") : "";
};

const getContainerYardSearchText = (yard) => {
  return [
    getContainerYardName(yard),
    yard && yard.shippingLineName,
    yard && yard.location,
    yard && yard.region
  ].filter(Boolean).join(" ").toLowerCase();
};

export const ContainerYardFilteredSelector = ({
  label,
  name,
  value,
  onChange,
  containerYards,
  classes,
  isRTL,
  t,
  multiple = false,
  disableUnavailable = false
}) => {
  const [shippingLineName, setShippingLineName] = useState("");
  const [region, setRegion] = useState("");
  const [searchText, setSearchText] = useState("");
  const fieldLabel = label || labelText(t, name);
  const selectedIds = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  const activeContainerYards = useMemo(() => (
    (containerYards || []).filter((yard) => yard && yard.active !== false)
  ), [containerYards]);
  const shippingLineOptions = useMemo(() => (
    Array.from(new Set(activeContainerYards.map((yard) => yard.shippingLineName).filter(Boolean)))
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" }))
  ), [activeContainerYards]);
  const filteredYards = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return activeContainerYards
      .filter((yard) => !shippingLineName || yard.shippingLineName === shippingLineName)
      .filter((yard) => !region || yard.region === region)
      .filter((yard) => !needle || getContainerYardSearchText(yard).indexOf(needle) !== -1)
      .sort((a, b) => getContainerYardName(a).localeCompare(getContainerYardName(b), undefined, { sensitivity: "base" }));
  }, [activeContainerYards, shippingLineName, region, searchText]);
  const selectedYards = activeContainerYards.filter((yard) => selectedIds.indexOf(yard.id) !== -1);
  const selectYard = (yard) => {
    if (!yard || !yard.id) return;
    const nextValue = multiple
      ? (selectedIds.indexOf(yard.id) !== -1 ? selectedIds.filter((id) => id !== yard.id) : [...selectedIds, yard.id])
      : yard.id;
    onChange({ target: { name, value: nextValue } });
  };
  return (
    <Grid container spacing={1} style={{ marginTop: 8 }}>
      <Grid item xs={12}>
        <Typography className={classes.typography} style={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
          {fieldLabel}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth margin="normal" variant="outlined" className={isRTL === 'rtl' ? classes.inputRtl : classes.textField}>
          <InputLabel id={`${name}-shipping-line-label`} style={{ fontFamily: FONT_FAMILY }}>{labelText(t, "shipping_line_name", "Shipping Line Name")}</InputLabel>
          <Select
            labelId={`${name}-shipping-line-label`}
            value={shippingLineName}
            onChange={(event) => setShippingLineName(event.target.value)}
            label={labelText(t, "shipping_line_name", "Shipping Line Name")}
            style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', fontFamily: FONT_FAMILY }}
          >
            <MenuItem value=""><em>{labelText(t, "all_shipping_lines", "All Shipping Lines")}</em></MenuItem>
            {shippingLineOptions.map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth margin="normal" variant="outlined" className={isRTL === 'rtl' ? classes.inputRtl : classes.textField}>
          <InputLabel id={`${name}-region-label`} style={{ fontFamily: FONT_FAMILY }}>{labelText(t, "region", "Region")}</InputLabel>
          <Select
            labelId={`${name}-region-label`}
            value={region}
            onChange={(event) => setRegion(event.target.value)}
            label={labelText(t, "region", "Region")}
            style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', fontFamily: FONT_FAMILY }}
          >
            <MenuItem value=""><em>{labelText(t, "all_regions", "All Regions")}</em></MenuItem>
            {CONTAINER_YARD_REGION_OPTIONS.map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
          variant="outlined"
          margin="normal"
          fullWidth
          label={labelText(t, "search_container_yard", "Search Container Yard")}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          className={isRTL === 'rtl' ? classes.inputRtl : classes.textField}
          style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
        />
      </Grid>
      {selectedYards.length > 0 ? (
        <Grid item xs={12}>
          <Typography className={classes.typography} style={{ fontFamily: FONT_FAMILY }}>
            {labelText(t, "selected", "Selected")}: {selectedYards.map((yard) => getContainerYardName(yard)).join(", ")}
          </Typography>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <div style={{ maxHeight: 260, overflowY: "auto", border: `1px solid ${SECONDORY_COLOR}`, borderRadius: 6, padding: 8 }}>
          {filteredYards.length > 0 ? filteredYards.map((yard) => {
            const selected = selectedIds.indexOf(yard.id) !== -1;
            const unavailable = disableUnavailable && (yard.availableSlots || 0) <= 0 && !selected;
            return (
              <Button
                key={yard.id}
                variant={selected ? "contained" : "outlined"}
                color="primary"
                disabled={unavailable}
                onClick={() => selectYard(yard)}
                style={{ width: "100%", justifyContent: "flex-start", marginBottom: 8, textAlign: "left", fontFamily: FONT_FAMILY }}
              >
                <span style={{ display: "block", whiteSpace: "normal", lineHeight: 1.35 }}>
                  <strong>{getContainerYardName(yard)}</strong>
                  <br />
                  {[yard.shippingLineName, yard.region, yard.location].filter(Boolean).join(" | ")}
                  <br />
                  {labelText(t, "available_slots", "Available Slots")}: {yard.availableSlots || 0} / {labelText(t, "total_slots", "Total Slots")}: {yard.totalSlots || 0}
                </span>
              </Button>
            );
          }) : (
            <Typography className={classes.typography} style={{ fontFamily: FONT_FAMILY, padding: 8 }}>
              {labelText(t, "no_records_found", "No Records To Display")}
            </Typography>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export const logisticsInstructionDefaults = {
  serviceType: "logistics_shipment",
  itemDescription: "",
  cargoLength: "",
  cargoWidth: "",
  cargoHeight: "",
  numberOfPieces: "",
  note: "",
  exactDeliveryAddress: "",
  favoriteTranspoType: "",
  favoriteTranspoId: "",
  favoriteTranspoName: "",
  favoriteTranspoCompanyName: "",
  preferredFleetAdminId: "",
  preferredFleetAdminName: "",
  pickupDeliveryInfo: {
    pickupAddress: "",
    deliveryAddress: "",
    receiverName: "",
    receiverContactNumber: "",
    preferredPickupDate: "",
    preferredPickupTime: ""
  },
  shipmentType: "",
  domesticBookingInfo: {
    bookingNumber: "",
    bookingDate: "",
    shipmentType: "",
    shipperName: "",
    consigneeName: "",
    contactPerson: "",
    contactNumber: "",
    completeDeliveryAddress: ""
  },
  importBookingInfo: {
    consigneeName: "",
    containerNumber: "",
    blNumber: "",
    bookingNumber: "",
    shippingLine: "",
    pickupPort: "",
    deliveryAddress: "",
    containerType: "",
    containerSize: "",
    freeTimeExpiry: "",
    freeTimeExpiryDate: "",
    lastFreeDay: "",
    detentionStartDate: "",
    detentionDays: "",
    detentionPenalty: "",
    detentionRemarks: "",
    detentionDeadline: "",
    demurrageDeadline: ""
  },
  exportBookingInfo: {
    exporterShipperName: "",
    portOfLoading: "",
    containerNumber: "",
    blNumber: "",
    bookingNumber: "",
    shippingLine: "",
    deliveryAddress: "",
    containerType: "",
    containerSize: "",
    vesselEtd: "",
    cargoCutOffDate: ""
  },
  containerNumber: "",
  additionalContainerNumbers: [],
  bookingNumber: "",
  blNumber: "",
  shippingLine: "",
  containerType: "",
  containerSize: "",
  grossWeight: "",
  pickupPort: "",
  deliveryWarehouse: "",
  pickupContainerYardId: "",
  pickupContainerYardName: "",
  pickupContainerYardSlots: "",
  emptyReturnContainerYardId: "",
  emptyReturnContainerYardIds: [],
  emptyReturnContainerYardName: "",
  emptyReturnContainerYardSlots: "",
  yardGateStatus: "",
  yardGateRejectionReason: "",
  freeTimeExpiry: "",
  detentionDeadline: "",
  demurrageDeadline: "",
  dateConfirmationOk: false
};

export const shipmentTypeOptions = [
  "Domestic Shipment",
  "Import Shipment",
  "Export Shipment"
];

export const serviceTypeOptions = [
  "logistics_shipment",
  "pickup_delivery"
];

export const shipmentTypeSelectorContainerSizes = ["20ft", "40ft"];

export const domesticShipmentTypeOptions = [
  "FCL",
  "LCL",
  "RORO"
];

const emptyDomesticBookingInfo = () => ({
  bookingNumber: "",
  bookingDate: "",
  shipmentType: "",
  shipperName: "",
  consigneeName: "",
  contactPerson: "",
  contactNumber: "",
  completeDeliveryAddress: ""
});

export const normalizeDomesticBookingInfo = (instructionData = {}) => ({
  ...emptyDomesticBookingInfo(),
  ...(instructionData.domesticBookingInfo || {})
});

const emptyImportBookingInfo = () => ({
  consigneeName: "",
  containerNumber: "",
  blNumber: "",
  bookingNumber: "",
  shippingLine: "",
  pickupPort: "",
  deliveryAddress: "",
  containerType: "",
  containerSize: "",
  freeTimeExpiry: "",
  freeTimeExpiryDate: "",
  lastFreeDay: "",
  detentionStartDate: "",
  detentionDays: "",
  detentionPenalty: "",
  detentionRemarks: "",
  detentionDeadline: "",
  demurrageDeadline: ""
});

const parseImportDetentionDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const startOfLocalDay = (date) => {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const toDateTimeInputValue = (date, sourceValue = "") => {
  if (!date || isNaN(date.getTime())) return "";
  const pad = (value) => value.toString().padStart(2, "0");
  const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  if (sourceValue && sourceValue.toString().indexOf("T") === -1) return datePart;
  return `${datePart}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getNextDayValue = (value) => {
  const parsed = parseImportDetentionDate(value);
  if (!parsed) return "";
  const nextDay = new Date(parsed.getTime());
  nextDay.setDate(nextDay.getDate() + 1);
  return toDateTimeInputValue(nextDay, value);
};

export const normalizeImportBookingInfo = (instructionData = {}, carDetails) => {
  const info = {
    ...emptyImportBookingInfo(),
    ...(instructionData.importBookingInfo || {})
  };
  info.freeTimeExpiryDate = info.freeTimeExpiryDate || info.freeTimeExpiry || instructionData.freeTimeExpiry || "";
  info.lastFreeDay = info.lastFreeDay || info.freeTimeExpiryDate || info.freeTimeExpiry || instructionData.freeTimeExpiry || "";
  info.detentionStartDate = info.detentionStartDate || getNextDayValue(info.lastFreeDay);
  const containerSize = getContainerSizeFromCarType(carDetails) || instructionData.containerSize || info.containerSize;
  if (containerSize) {
    info.containerSize = containerSize;
  }
  return info;
};

export const getImportDetentionStatus = ({
  booking = {},
  importBookingInfo,
  returnRecord,
  emptyReturnClosed,
  emptyReturnAcceptedAt,
  emptyContainerReturnLastStatus,
  now = new Date()
} = {}) => {
  const info = normalizeImportBookingInfo({
    ...(booking || {}),
    importBookingInfo: importBookingInfo || (booking && booking.importBookingInfo) || {}
  }, booking && booking.carType);
  const statusValue = (emptyContainerReturnLastStatus || (returnRecord && (returnRecord.emptyReturnStatus || returnRecord.status)) || "").toString().trim().toLowerCase();
  const acceptedRecord = statusValue === "accepted";
  const acceptedReturn = booking && booking.emptyContainerReturns
    ? Object.values(booking.emptyContainerReturns).some((entry) => {
      const value = ((entry && (entry.emptyReturnStatus || entry.status)) || "").toString().trim().toLowerCase();
      return value === "accepted";
    })
    : false;
  const acceptedAt = emptyReturnAcceptedAt || (returnRecord && (returnRecord.emptyReturnAcceptedAt || (acceptedRecord ? (returnRecord.emptyReturnCompletedAt || returnRecord.acceptanceDate) : "")));
  const returnedOrClosed = emptyReturnClosed === true || !!acceptedAt || acceptedRecord || acceptedReturn;
  const lastFreeDayValue = info.lastFreeDay || info.freeTimeExpiryDate || info.freeTimeExpiry || "";
  const lastFreeDayDate = parseImportDetentionDate(lastFreeDayValue);
  const today = startOfLocalDay(parseImportDetentionDate(now) || new Date());
  const lastFreeDay = startOfLocalDay(lastFreeDayDate);
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = lastFreeDay ? Math.round((lastFreeDay.getTime() - today.getTime()) / dayMs) : null;
  let detentionStatus = "No free-time date";
  let daysRemaining = null;
  let overdueDays = null;
  if (returnedOrClosed) {
    detentionStatus = "Returned / Closed";
  } else if (lastFreeDay) {
    if (diffDays > 0) {
      detentionStatus = "Safe";
      daysRemaining = diffDays;
    } else if (diffDays === 0) {
      detentionStatus = "Due Today";
      daysRemaining = 0;
    } else {
      detentionStatus = "Overdue";
      overdueDays = Math.abs(diffDays);
    }
  }
  return {
    ...info,
    lastFreeDay: lastFreeDayValue,
    detentionStartDate: info.detentionStartDate || getNextDayValue(lastFreeDayValue),
    daysRemaining,
    isDueToday: detentionStatus === "Due Today",
    isOverdue: detentionStatus === "Overdue",
    overdueDays,
    detentionStatus
  };
};

export const findWorkflowConfiguration = (workflowConfigs, instructionData = {}, carDetails = {}) => {
  const vehicleTypeId = carDetails && (carDetails.id || carDetails.vehicleTypeId || carDetails.uid) ? (carDetails.id || carDetails.vehicleTypeId || carDetails.uid).toString() : "";
  const vehicleTypeName = typeof carDetails === "string" ? carDetails : (carDetails && (carDetails.name || carDetails.carType || carDetails.vehicleTypeName)) || "";
  const vehicleSize = getContainerSizeFromCarType(carDetails) || instructionData.containerSize || getVehicleSize({
    vehicleTypeName,
    carType: vehicleTypeName
  });
  return findWorkflowConfig(workflowConfigs, {
    vehicleTypeId,
    vehicleTypeName,
    carType: vehicleTypeName,
    vehicleSize,
    shipmentType: instructionData.shipmentType || (vehicleSize === "10ft" ? "Domestic Shipment" : "")
  });
};

export const getBookingWorkflowForBooking = (instructionData = {}, carDetails = {}, workflowConfigs = null, status = "") => {
  const vehicleTypeName = typeof carDetails === "string" ? carDetails : (carDetails && (carDetails.name || carDetails.carType || carDetails.vehicleTypeName)) || "";
  const vehicleSize = getContainerSizeFromCarType(carDetails) || instructionData.containerSize || getVehicleSize({
    vehicleTypeName,
    carType: vehicleTypeName
  });
  const workflowConfig = findWorkflowConfiguration(workflowConfigs, instructionData, carDetails);
  return getBookingWorkflow({
    vehicleTypeId: carDetails && (carDetails.id || carDetails.vehicleTypeId || carDetails.uid) ? (carDetails.id || carDetails.vehicleTypeId || carDetails.uid) : "",
    vehicleTypeName,
    carType: vehicleTypeName,
    vehicleSize,
    shipmentType: instructionData.shipmentType || (vehicleSize === "10ft" ? "Domestic Shipment" : ""),
    serviceType: instructionData.serviceType,
    status,
    workflowConfig
  });
};

export const shouldShowDomesticBookingInfo = (instructionData = {}, carDetails, workflowConfigs = null) => {
  return !!getBookingWorkflowForBooking(instructionData, carDetails, workflowConfigs).showDomesticForm;
};

export const shouldShowImportBookingInfo = (instructionData = {}, carDetails, workflowConfigs = null) => {
  return !!getBookingWorkflowForBooking(instructionData, carDetails, workflowConfigs).showImportForm;
};

const emptyExportBookingInfo = () => ({
  exporterShipperName: "",
  portOfLoading: "",
  containerNumber: "",
  blNumber: "",
  bookingNumber: "",
  shippingLine: "",
  deliveryAddress: "",
  containerType: "",
  containerSize: "",
  vesselEtd: "",
  cargoCutOffDate: ""
});

export const normalizeExportBookingInfo = (instructionData = {}, carDetails) => {
  const info = {
    ...emptyExportBookingInfo(),
    ...(instructionData.exportBookingInfo || {})
  };
  const containerSize = getContainerSizeFromCarType(carDetails) || instructionData.containerSize || info.containerSize;
  if (containerSize) {
    info.containerSize = containerSize;
  }
  return info;
};

export const shouldShowExportBookingInfo = (instructionData = {}, carDetails, workflowConfigs = null) => {
  return !!getBookingWorkflowForBooking(instructionData, carDetails, workflowConfigs).showExportForm;
};

const emptyPickupDeliveryInfo = () => ({
  pickupAddress: "",
  deliveryAddress: "",
  receiverName: "",
  receiverContactNumber: "",
  preferredPickupDate: "",
  preferredPickupTime: ""
});

export const normalizeServiceType = (value) => {
  const normalized = (value || "").toString().trim().toLowerCase().replace(/[^a-z]/g, "_");
  if (normalized === "pickup_delivery" || normalized === "pickup___delivery" || normalized === "pickupdelivery") {
    return "pickup_delivery";
  }
  return "logistics_shipment";
};

export const isPickupDeliveryService = (instructionData = {}) => normalizeServiceType(instructionData.serviceType) === "pickup_delivery";

export const normalizePickupDeliveryInfo = (instructionData = {}) => ({
  ...emptyPickupDeliveryInfo(),
  ...(instructionData.pickupDeliveryInfo || {})
});

export const shouldShowPickupDeliveryInfo = (instructionData = {}, carDetails) => {
  return isPickupDeliveryService(instructionData);
};

export const getAvailableServiceTypeOptions = (carDetails, instructionData = {}) => {
  return serviceTypeOptions;
};

const generalCargoVehicleTypeNames = [
  "L300/FB/VAN",
  "TRUCK 3TONS",
  "WINGVAN 7TONS",
  "BOOM TRUCK 2TONS",
  "TOWING TRUCK"
];

const normalizeVehicleTypeName = (value) => (value || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");

export const isGeneralCargoVehicleType = (carDetails) => {
  const name = typeof carDetails === "string" ? carDetails : (carDetails && carDetails.name ? carDetails.name : "");
  const normalized = normalizeVehicleTypeName(name);
  return generalCargoVehicleTypeNames.some((item) => {
    const target = normalizeVehicleTypeName(item);
    return normalized === target || normalized.indexOf(target) !== -1;
  });
};

export const getCargoDimensions = (booking) => {
  if (!booking) return "";
  if (booking.cargoDimensions) return booking.cargoDimensions;
  const values = [booking.cargoLength, booking.cargoWidth, booking.cargoHeight].map((value) => value || "");
  return values.some((value) => value) ? values.join(" x ") : "";
};

export const sanitizeInstructionDataForVehicle = (instructionData = {}, carDetails, workflowConfigs = null) => {
  const sanitized = { ...instructionData };
  const containerSize = getContainerSizeFromCarType(carDetails);
  sanitized.serviceType = normalizeServiceType(sanitized.serviceType);
  sanitized.pickupDeliveryInfo = normalizePickupDeliveryInfo(sanitized);
  const containerFields = [
    "containerNumber",
    "additionalContainerNumbers",
    "bookingNumber",
    "blNumber",
    "shippingLine",
    "containerType",
    "containerSize",
    "pickupPort",
    "deliveryWarehouse",
    "pickupContainerYardId",
    "pickupContainerYardName",
    "pickupContainerYardSlots",
    "emptyReturnContainerYardId",
    "emptyReturnContainerYardIds",
    "emptyReturnContainerYardName",
    "emptyReturnContainerYardSlots",
    "yardGateStatus",
    "yardGateRejectionReason",
    "freeTimeExpiry",
    "detentionDeadline",
    "demurrageDeadline",
    "dateConfirmationOk",
    "shipmentType"
  ];
  const clearFields = (fields) => fields.forEach((field) => {
    sanitized[field] = Array.isArray(sanitized[field]) ? [] : "";
  });
  const containerShippingInfoFields = containerFields.filter((field) => field !== "shipmentType");
  const clearContainerShippingInfoFields = () => {
    clearFields(containerShippingInfoFields);
    sanitized.additionalContainerNumbers = [];
    sanitized.emptyReturnContainerYardIds = [];
    sanitized.dateConfirmationOk = false;
  };
  if (shipmentTypeSelectorContainerSizes.indexOf(containerSize) !== -1) {
    sanitized.serviceType = "logistics_shipment";
  }
  if (isPickupDeliveryService(sanitized)) {
    clearFields(containerFields);
    sanitized.shipmentType = "";
    sanitized.dateConfirmationOk = false;
    sanitized.containerSize = containerSize && containerSize === "10ft" ? "10ft" : "";
    sanitized.domesticBookingInfo = emptyDomesticBookingInfo();
    sanitized.importBookingInfo = emptyImportBookingInfo();
    sanitized.exportBookingInfo = emptyExportBookingInfo();
    sanitized.pickupDeliveryInfo = normalizePickupDeliveryInfo(sanitized);
    return sanitized;
  }
  if (containerSize) {
    if (containerSize === "10ft") {
      clearFields(containerFields);
      sanitized.additionalContainerNumbers = [];
      sanitized.emptyReturnContainerYardIds = [];
      sanitized.shipmentType = "Domestic Shipment";
    } else if (shipmentTypeSelectorContainerSizes.indexOf(containerSize) !== -1 && shipmentTypeOptions.indexOf(sanitized.shipmentType) === -1) {
      sanitized.containerSize = sanitized.containerSize || containerSize;
      sanitized.shipmentType = "";
    } else {
      sanitized.containerSize = sanitized.containerSize || containerSize;
    }
  } else {
    clearFields(containerFields);
    sanitized.shipmentType = "";
    sanitized.dateConfirmationOk = false;
  }
  const workflow = getBookingWorkflowForBooking(sanitized, carDetails, workflowConfigs);
  if (workflow.shouldClearContainerShippingInfo) {
    clearContainerShippingInfoFields();
  }
  if (workflow.showDomesticForm) {
    sanitized.domesticBookingInfo = normalizeDomesticBookingInfo(sanitized);
    clearContainerShippingInfoFields();
    sanitized.shipmentType = "Domestic Shipment";
  } else {
    sanitized.domesticBookingInfo = emptyDomesticBookingInfo();
  }
  if (workflow.showImportForm) {
    const importInfo = normalizeImportBookingInfo(sanitized, carDetails);
    sanitized.importBookingInfo = importInfo;
    sanitized.containerNumber = importInfo.containerNumber;
    sanitized.bookingNumber = importInfo.bookingNumber;
    sanitized.blNumber = importInfo.blNumber;
    sanitized.shippingLine = importInfo.shippingLine;
    sanitized.pickupPort = importInfo.pickupPort;
    sanitized.deliveryWarehouse = importInfo.deliveryAddress;
    sanitized.containerType = importInfo.containerType;
    sanitized.containerSize = importInfo.containerSize || sanitized.containerSize;
    sanitized.freeTimeExpiry = importInfo.freeTimeExpiry;
    sanitized.detentionDeadline = importInfo.detentionDeadline;
    sanitized.demurrageDeadline = importInfo.demurrageDeadline;
  } else {
    sanitized.importBookingInfo = emptyImportBookingInfo();
  }
  if (workflow.showExportForm) {
    const exportInfo = normalizeExportBookingInfo(sanitized, carDetails);
    sanitized.exportBookingInfo = exportInfo;
    sanitized.containerNumber = exportInfo.containerNumber;
    sanitized.bookingNumber = exportInfo.bookingNumber;
    sanitized.blNumber = exportInfo.blNumber;
    sanitized.shippingLine = exportInfo.shippingLine;
    sanitized.pickupPort = exportInfo.portOfLoading;
    sanitized.deliveryWarehouse = exportInfo.deliveryAddress;
    sanitized.containerType = exportInfo.containerType;
    sanitized.containerSize = exportInfo.containerSize || sanitized.containerSize;
  } else {
    sanitized.exportBookingInfo = emptyExportBookingInfo();
  }
  return sanitized;
};

export const containerTypeOptions = [
  "Dry",
  "Reefer",
  "Open Top",
  "Flat Rack",
  "ISO Tank",
  "Ventilated",
  "High Cube"
];

export const getContainerSizeFromCarType = (carDetails) => {
  const name = typeof carDetails === "string" ? carDetails : (carDetails && carDetails.name ? carDetails.name : "");
  const normalized = name.toLowerCase();
  if (/(^|[^0-9])10\s*(ft|footer|feet)?([^0-9]|$)/.test(normalized)) return "10ft";
  if (/(^|[^0-9])20\s*(ft|footer|feet)?([^0-9]|$)/.test(normalized)) return "20ft";
  if (/(^|[^0-9])40\s*(ft|footer|feet)?([^0-9]|$)/.test(normalized)) return "40ft";
  return "";
};

export const isContainerTruckBooking = (booking) => {
  if (!booking) return false;
  const carTypeSize = getContainerSizeFromCarType(booking.carType || "");
  if (booking.carType && !carTypeSize) return false;
  const size = (carTypeSize || booking.containerSize || "").toString().toLowerCase();
  return ["10ft", "20ft", "40ft"].indexOf(size) !== -1;
};

export const isDriverEligibleForBookingCar = (driverCarType, bookingCarDetails) => {
  const driverContainerSize = getContainerSizeFromCarType(driverCarType);
  const bookingContainerSize = getContainerSizeFromCarType(bookingCarDetails);
  if (bookingContainerSize) {
    return !!driverContainerSize;
  }
  const bookingCarName = typeof bookingCarDetails === "string" ? bookingCarDetails : (bookingCarDetails && bookingCarDetails.name ? bookingCarDetails.name : "");
  return (driverCarType || "") === (bookingCarName || "");
};

export const generalCargoDisplayFields = [
  "itemDescription",
  "grossWeight",
  "cargoDimensions",
  "numberOfPieces"
];

const logisticsTextFields = [
  "containerNumber",
  "bookingNumber",
  "blNumber",
  "shippingLine",
  "pickupPort",
  "deliveryWarehouse"
];

const generalCargoTextFields = [
  "itemDescription",
  "grossWeight",
  "numberOfPieces"
];

const containerBookingTextFields = [
  "itemDescription",
  "grossWeight",
  "numberOfPieces"
];

const primaryRequiredFields = [
  "itemDescription",
  "grossWeight",
  "numberOfPieces",
  "cargoLength",
  "cargoWidth",
  "cargoHeight",
  "exactDeliveryAddress"
];

const primaryNumericFields = [
  "grossWeight",
  "numberOfPieces",
  "cargoLength",
  "cargoWidth",
  "cargoHeight"
];

const domesticRequiredFields = [
  "bookingNumber",
  "bookingDate",
  "shipmentType",
  "shipperName",
  "consigneeName",
  "contactPerson",
  "contactNumber",
  "completeDeliveryAddress"
];

const domesticLabelKeys = {
  bookingNumber: "domesticBookingNumber",
  bookingDate: "domesticBookingDate",
  shipmentType: "domesticShipmentType",
  shipperName: "shipperName",
  consigneeName: "consigneeName",
  contactPerson: "contactPerson",
  contactNumber: "contactNumber",
  completeDeliveryAddress: "completeDeliveryAddress"
};

const importRequiredFields = [
  "consigneeName",
  "containerNumber",
  "blNumber",
  "bookingNumber",
  "shippingLine",
  "pickupPort",
  "deliveryAddress",
  "containerType",
  "containerSize",
  "freeTimeExpiry",
  "detentionDeadline",
  "demurrageDeadline"
];

const importLabelKeys = {
  consigneeName: "consigneeName",
  containerNumber: "containerNumber",
  blNumber: "blAwbNumber",
  bookingNumber: "bookingNumber",
  shippingLine: "shippingLine",
  pickupPort: "pickupPort",
  deliveryAddress: "deliveryWarehouse",
  containerType: "containerType",
  containerSize: "containerSize",
  freeTimeExpiry: "freeTimeExpiry",
  detentionDeadline: "detentionDeadline",
  demurrageDeadline: "demurrageDeadline"
};

const exportRequiredFields = [
  "exporterShipperName",
  "portOfLoading",
  "containerNumber",
  "blNumber",
  "bookingNumber",
  "shippingLine",
  "deliveryAddress",
  "containerType",
  "containerSize",
  "vesselEtd",
  "cargoCutOffDate"
];

const exportLabelKeys = {
  exporterShipperName: "exporterShipperName",
  portOfLoading: "portOfLoading",
  containerNumber: "containerNumber",
  blNumber: "blAwbNumber",
  bookingNumber: "bookingNumber",
  shippingLine: "shippingLine",
  deliveryAddress: "deliveryWarehouse",
  containerType: "containerType",
  containerSize: "containerSize",
  vesselEtd: "vesselEtd",
  cargoCutOffDate: "cargoCutOffDate"
};

export const isValidContactNumber = (value) => {
  const text = (value || "").toString().trim();
  const digits = text.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 20 && /^[0-9+\-()\s]+$/.test(text);
};

export const hasAcceptedDriver = (booking) => {
  if (!booking) return false;
  const status = (booking.status || "").toString().toUpperCase();
  const acceptedStatuses = ["ACCEPTED", "STARTED", "REACHED", "ARRIVED", "COMPLETE", "PAID"];
  return !!(booking.driver || booking.driver_name || booking.driverDetails || booking.driverId || booking.driver_id || acceptedStatuses.indexOf(status) !== -1);
};

export const validatePrimaryBookingInformation = (t, instructionData = {}, carDetails, workflowConfigs = null) => {
  const data = sanitizeInstructionDataForVehicle(instructionData, carDetails, workflowConfigs);
  const workflow = getBookingWorkflowForBooking(data, carDetails, workflowConfigs);
  const isBlank = (value) => value === null || value === undefined || value.toString().trim() === "";
  const sectionName = labelText(t, "primary_booking_information_form", "Primary Booking Information Form");
  const requiredFields = isPickupDeliveryService(data)
    ? primaryRequiredFields.filter((field) => field !== "exactDeliveryAddress")
    : primaryRequiredFields;
  for (let i = 0; i < requiredFields.length; i++) {
    const field = requiredFields[i];
    if (isBlank(data[field])) {
      return `${sectionName}: ${labelText(t, field)} is required.`;
    }
  }
  for (let i = 0; i < primaryNumericFields.length; i++) {
    const field = primaryNumericFields[i];
    if (!isBlank(data[field]) && !Number.isFinite(Number(data[field]))) {
      return `${sectionName}: ${labelText(t, field)} must be numeric.`;
    }
  }
  if ((data.note || "").toString().length > 70) {
    return `${sectionName}: ${labelText(t, "note")} must be 70 characters or less.`;
  }
  if (!isPickupDeliveryService(data) && workflow.showShipmentTypeSelector && shipmentTypeOptions.indexOf(data.shipmentType) === -1) {
    return `${sectionName}: ${labelText(t, "shipmentType")} is required.`;
  }
  return "";
};

export const validateDomesticBookingInfo = (t, instructionData = {}, carDetails, workflowConfigs = null) => {
  const data = sanitizeInstructionDataForVehicle(instructionData, carDetails, workflowConfigs);
  if (isPickupDeliveryService(data)) return "";
  if (!getBookingWorkflowForBooking(data, carDetails, workflowConfigs).showDomesticForm) return "";
  const domesticInfo = normalizeDomesticBookingInfo(data);
  const isBlank = (value) => value === null || value === undefined || value.toString().trim() === "";
  const sectionName = labelText(t, "domestic_booking_information_form", "Domestic Booking Information Form");
  for (let i = 0; i < domesticRequiredFields.length; i++) {
    const field = domesticRequiredFields[i];
    if (isBlank(domesticInfo[field])) {
      return `${sectionName}: ${labelText(t, domesticLabelKeys[field])} is required.`;
    }
  }
  if (domesticShipmentTypeOptions.indexOf(domesticInfo.shipmentType) === -1) {
    return `${sectionName}: ${labelText(t, "domesticShipmentType")} is required.`;
  }
  if (!isValidContactNumber(domesticInfo.contactNumber)) {
    return `${sectionName}: ${labelText(t, "contactNumber")} is invalid.`;
  }
  return "";
};

export const validateImportBookingInfo = (t, instructionData = {}, carDetails, workflowConfigs = null) => {
  const data = sanitizeInstructionDataForVehicle(instructionData, carDetails, workflowConfigs);
  if (isPickupDeliveryService(data)) return "";
  if (!getBookingWorkflowForBooking(data, carDetails, workflowConfigs).showImportForm) return "";
  const importInfo = normalizeImportBookingInfo(data, carDetails);
  const isBlank = (value) => value === null || value === undefined || value.toString().trim() === "";
  const sectionName = labelText(t, "import_booking_information_form", "Import Booking Information Form");
  for (let i = 0; i < importRequiredFields.length; i++) {
    const field = importRequiredFields[i];
    if (isBlank(importInfo[field])) {
      return `${sectionName}: ${labelText(t, importLabelKeys[field])} is required.`;
    }
  }
  if (containerTypeOptions.indexOf(importInfo.containerType) === -1) {
    return `${sectionName}: ${labelText(t, "containerType")} is required.`;
  }
  return "";
};

export const validateExportBookingInfo = (t, instructionData = {}, carDetails, workflowConfigs = null) => {
  const data = sanitizeInstructionDataForVehicle(instructionData, carDetails, workflowConfigs);
  if (isPickupDeliveryService(data)) return "";
  if (!getBookingWorkflowForBooking(data, carDetails, workflowConfigs).showExportForm) return "";
  const exportInfo = normalizeExportBookingInfo(data, carDetails);
  const isBlank = (value) => value === null || value === undefined || value.toString().trim() === "";
  const sectionName = labelText(t, "export_booking_information_form", "Export Booking Information Form");
  for (let i = 0; i < exportRequiredFields.length; i++) {
    const field = exportRequiredFields[i];
    if (isBlank(exportInfo[field])) {
      return `${sectionName}: ${labelText(t, exportLabelKeys[field])} is required.`;
    }
  }
  if (containerTypeOptions.indexOf(exportInfo.containerType) === -1) {
    return `${sectionName}: ${labelText(t, "containerType")} is required.`;
  }
  return "";
};

export const validatePickupDeliveryInfo = (t, instructionData = {}, carDetails, workflowConfigs = null) => {
  const data = sanitizeInstructionDataForVehicle(instructionData, carDetails, workflowConfigs);
  if (!shouldShowPickupDeliveryInfo(data, carDetails)) return "";
  const pickupDeliveryInfo = normalizePickupDeliveryInfo(data);
  const requiredFields = ["pickupAddress", "deliveryAddress", "receiverName", "receiverContactNumber"];
  const isBlank = (value) => value === null || value === undefined || value.toString().trim() === "";
  const sectionName = labelText(t, "pickup_delivery_information_form", "Pickup & Delivery Information");
  for (let i = 0; i < requiredFields.length; i++) {
    const field = requiredFields[i];
    if (isBlank(pickupDeliveryInfo[field])) {
      return `${sectionName}: ${labelText(t, field)} is required.`;
    }
  }
  if (!isValidContactNumber(pickupDeliveryInfo.receiverContactNumber)) {
    return `${sectionName}: ${labelText(t, "receiverContactNumber")} is invalid.`;
  }
  return "";
};

const cargoDimensionFields = [
  "cargoLength",
  "cargoWidth",
  "cargoHeight"
];

const domesticBookingTextFields = [
  { field: "bookingNumber", labelKey: "domesticBookingNumber" },
  { field: "shipperName", labelKey: "shipperName" },
  { field: "consigneeName", labelKey: "consigneeName" },
  { field: "contactPerson", labelKey: "contactPerson" },
  { field: "contactNumber", labelKey: "contactNumber" },
  { field: "completeDeliveryAddress", labelKey: "completeDeliveryAddress", multiline: true }
];

const importBookingTextFields = [
  { field: "consigneeName", labelKey: "consigneeName" },
  { field: "containerNumber", labelKey: "containerNumber" },
  { field: "blNumber", labelKey: "blAwbNumber" },
  { field: "bookingNumber", labelKey: "bookingNumber" },
  { field: "shippingLine", labelKey: "shippingLine" },
  { field: "pickupPort", labelKey: "pickupPort" },
  { field: "deliveryAddress", labelKey: "deliveryWarehouse", multiline: true }
];

const exportBookingTextFields = [
  { field: "exporterShipperName", labelKey: "exporterShipperName" },
  { field: "portOfLoading", labelKey: "portOfLoading" },
  { field: "containerNumber", labelKey: "containerNumber" },
  { field: "blNumber", labelKey: "blAwbNumber" },
  { field: "bookingNumber", labelKey: "bookingNumber" },
  { field: "shippingLine", labelKey: "shippingLine" },
  { field: "deliveryAddress", labelKey: "deliveryWarehouse", multiline: true }
];

const logisticsDateFields = [
  "freeTimeExpiry",
  "detentionDeadline",
  "demurrageDeadline"
];

const exportDateFields = [
  "vesselEtd",
  "cargoCutOffDate"
];

const uniqueFields = (fields) => Array.from(new Set(fields));

const getBidAmountDisplay = (booking, settings, t) => {
  const amount = getFinalBidAmount(booking);
  if (amount !== null && amount !== undefined && amount !== "") {
    return formatCurrency(amount, settings);
  }
  if (booking && booking.deliveryWithBid === true) {
    return labelText(t, "waiting_for_bid_acceptance", "Waiting for bid acceptance");
  }
  return formatCurrency(0, settings);
};

export const getCommissionTypeDisplay = (booking, t) => {
  const preview = getCommissionPreview(booking);
  if (preview.status === "pending") return labelText(t, "pending", "Pending");
  if (preview.status === "not_configured") return labelText(t, "not_configured", "Not configured");
  if (preview.type === "percentage") return labelText(t, "percentage", "Percentage");
  return labelText(t, "flat_rate", "Flat Rate");
};

export const getCommissionRateDisplay = (booking, t) => {
  const preview = getCommissionPreview(booking);
  if (preview.status === "pending") return labelText(t, "pending", "Pending");
  if (preview.status === "not_configured") return labelText(t, "not_configured", "Not configured");
  if (preview.rate === null || preview.rate === undefined || preview.rate === "") return labelText(t, "not_configured", "Not configured");
  return preview.type === "percentage" ? `${preview.rate}%` : preview.rate;
};

export const getCommissionAmountDisplay = (booking, settings, t) => {
  const preview = getCommissionPreview(booking);
  if (preview.status === "pending") return labelText(t, "pending", "Pending");
  if (preview.status === "not_configured" || preview.commissionAmount === null || preview.commissionAmount === undefined) return labelText(t, "pending", "Pending");
  return formatCurrency(preview.commissionAmount, settings);
};

export const getEstimatedPayeeDisplay = (booking, t) => {
  const preview = getEarningsPreview(booking);
  if (preview.status === "pending") return labelText(t, "pending", "Pending");
  if (preview.status === "handled_by_fleet_admin") return labelText(t, "handled_by_fleet_admin", "Handled by Fleet Admin");
  if (preview.payeeType === "fleet") return preview.payeeName || labelText(t, "logistics_company", "Logistics Company");
  if (preview.payeeType === "driver") return preview.payeeName || labelText(t, "independent_driver", "Independent Driver");
  return labelText(t, "not_configured", "Not configured");
};

export const getEstimatedEarningsDisplay = (booking, settings, t) => {
  const preview = getEarningsPreview(booking);
  if (preview.status === "handled_by_fleet_admin") return labelText(t, "handled_by_fleet_admin", "Handled by Fleet Admin");
  if (preview.status === "pending") return labelText(t, "pending", "Pending");
  if (preview.netAmount === null || preview.netAmount === undefined) return labelText(t, "pending", "Pending");
  return formatCurrency(preview.netAmount, settings);
};

const getOtpStatusDisplay = (booking, t) => {
  if (!booking) return labelText(t, "not_available", "Not available");
  const status = (booking.status || "").toString().toUpperCase();
  const hasOtp = !!booking.otp;
  const otpVerified = booking.otpVerified || booking.otp_verified || booking.otpMatched || booking.otpUsed || booking.startOtpVerified;
  if (status === "CANCELLED" || status === "NEW" || status === "AWAITING_DRIVER_ASSIGNMENT") {
    return labelText(t, "inactive", "Inactive");
  }
  if (status === "COMPLETE" || status === "COMPLETED" || status === "PAID") {
    return otpVerified || hasOtp ? labelText(t, "used_verified", "Used / Verified") : labelText(t, "inactive", "Inactive");
  }
  if (status === "STARTED" || status === "ARRIVED" || status === "REACHED") {
    return otpVerified ? labelText(t, "used_verified", "Used / Verified") : (hasOtp ? labelText(t, "active", "Active") : labelText(t, "not_available", "Not available"));
  }
  if (status === "ACCEPTED" || booking.driver || booking.driver_name) {
    return hasOtp ? labelText(t, "active", "Active") : labelText(t, "not_available", "Not available");
  }
  return hasOtp ? labelText(t, "active", "Active") : labelText(t, "not_available", "Not available");
};

const getFleetAdminBookingHistoryColumns = (settings, t) => [
  { title: t('booking_ref'), field: 'reference'},
  { title: t('booking_date'), field: 'bookingDate', render: rowData => rowData.bookingDate?moment(rowData.bookingDate).format('lll'):null,},
  { title: t('car_type'), field: 'carType',  },
  { title: t('assign_driver'), field: 'driver_name', },
  { title: t('booking_status_web'), field: 'status',
   render: rowData =>
  <div
  style={{backgroundColor:getBookingStatusColor(rowData), color:"white", padding:7, borderRadius:"15px", fontWeight:"bold", width:"170px", margin: 'auto' }}
  >{getBookingStatusLabel(rowData, "fleetadmin", t)}</div>,  },
  { title: labelText(t, 'otp_status', 'OTP Status'), field: 'otpStatus',  render: rowData => <div>{getOtpStatusDisplay(rowData, t)}</div> },
  { title: labelText(t, 'bid_amount', 'Bid Amount'), field: 'bidAmount', render: rowData => getBidAmountDisplay(rowData, settings, t) },
  { title: labelText(t, 'commission_type', 'Commission Type'), field: 'commissionType', render: rowData => getCommissionTypeDisplay(rowData, t) },
  { title: labelText(t, 'commission_rate', 'Commission Rate'), field: 'commissionRate', render: rowData => getCommissionRateDisplay(rowData, t) },
  { title: labelText(t, 'commission_amount', 'Commission Amount'), field: 'fleetCommission', render: rowData => getCommissionAmountDisplay(rowData, settings, t) },
  { title: labelText(t, 'estimated_earnings', 'Estimated Earnings'), field: 'estimatedEarnings', render: rowData => getEstimatedEarningsDisplay(rowData, settings, t) },
];

export const bookingHistoryColumns = (role, settings, t, isRTL, profile = {}) => [
  ...(role === "fleetadmin" ? getFleetAdminBookingHistoryColumns(settings, t) : [
  { title: t('booking_ref'), field: 'reference'},
  { title: t('booking_date'), field: 'bookingDate', render: rowData => rowData.bookingDate?moment(rowData.bookingDate).format('lll'):null,},
  { title: t('car_type'), field: 'carType',  },
  { title: t('assign_driver'), field: 'driver_name', },
  { title: t('booking_status_web'), field: 'status',
   render: rowData => 
  <div
  style={{backgroundColor:getBookingStatusColor(rowData), color:"white", padding:7, borderRadius:"15px", fontWeight:"bold", width:"170px", margin: 'auto' }}
  >{getBookingStatusLabel(rowData, role, t, profile)}</div>,  },
  ...(role === "admin" ? [
    { title: labelText(t, 'accepted_offer', 'Accepted Offer'), field: 'acceptedOfferType', render: rowData => getAcceptedOfferSummary(rowData, settings, t) || "-" },
    { title: labelText(t, 'commission_type', 'Commission Type'), field: 'commissionType', render: rowData => getCommissionTypeDisplay(rowData, t) },
    { title: labelText(t, 'commission_amount', 'Commission Amount'), field: 'commissionAmount', render: rowData => getCommissionAmountDisplay(rowData, settings, t) },
    { title: labelText(t, 'estimated_payee', 'Estimated Payee'), field: 'estimatedPayee', render: rowData => getEstimatedPayeeDisplay(rowData, t) },
    { title: labelText(t, 'estimated_net_earnings', 'Estimated Net Earnings'), field: 'estimatedNetEarnings', render: rowData => getEstimatedEarningsDisplay(rowData, settings, t) },
  ] : []),
  ...(role === "customer" ? [
    { title: labelText(t, 'payment_status', 'Payment Status'), field: 'paymentStatus', render: rowData => getBookingStatusLabel(rowData, role, t, profile) },
  ] : []),
  { title: t('otp'), field: 'otp',  render: rowData => <div>{rowData.otp ? t("true") : t("false")}</div> },
  {
    title: role === "customer" ? labelText(t, 'total_payable', 'Total Payable') : (role === "driver" && !(profile && profile.fleetadmin) ? labelText(t, 'bid_amount', 'Bid Amount') : (role === "admin" ? labelText(t, 'bid_amount', 'Bid Amount') : t('trip_cost'))),
    field: 'trip_cost',
    render: (rowData) => {
      return rowData.deliveryWithBid === true ? getBidAmountDisplay(rowData, settings, t) : formatCurrency(rowData.trip_cost ? rowData.trip_cost : 0, settings);
    },
  },
  ...(role === "driver" && !(profile && profile.fleetadmin) ? [
    { title: labelText(t, 'commission_amount', 'Commission Amount'), field: 'commissionAmount', render: rowData => getCommissionAmountDisplay(rowData, settings, t) },
    { title: labelText(t, 'estimated_driver_earnings', 'Estimated Driver Earnings'), field: 'estimatedDriverEarnings', render: rowData => getEstimatedEarningsDisplay(rowData, settings, t) },
  ] : []),
  ])
];

export const BookingModalBody = (props) => {
    const { t, i18n  } = useTranslation();
    const isRTL = i18n.dir();
    const { classes, instructionData, handleChange, auth, profileData, settings, deliveryWithBid, otherPerson, setOtherPerson, containerYards, selectedCarDetails, workflowConfigs, hasAddedStops } = props;
    const activeContainerYards = containerYards ? containerYards.filter((yard) => yard.active !== false) : [];
    const selectedCarContainerSize = getContainerSizeFromCarType(selectedCarDetails);
    const showContainerInfo = shipmentTypeSelectorContainerSizes.indexOf(selectedCarContainerSize) !== -1;
    const showBookingInformation = !!selectedCarDetails;
    const availableServiceTypes = hasAddedStops ? ["pickup_delivery"] : getAvailableServiceTypeOptions(selectedCarDetails, instructionData);
    const pickupDeliveryInfo = normalizePickupDeliveryInfo(instructionData);
    const showPickupDeliveryInfo = showBookingInformation && shouldShowPickupDeliveryInfo(instructionData, selectedCarDetails);
    const workflow = getBookingWorkflowForBooking(instructionData, selectedCarDetails, workflowConfigs);
    const showPrimaryInfo = showBookingInformation && workflow.showPrimaryForm;
    const showShipmentTypeSelector = showBookingInformation && workflow.showShipmentTypeSelector;
    const showDomesticShipmentSelector = false;
    const domesticBookingInfo = normalizeDomesticBookingInfo(instructionData);
    const importBookingInfo = normalizeImportBookingInfo(instructionData, selectedCarDetails);
    const exportBookingInfo = normalizeExportBookingInfo(instructionData, selectedCarDetails);
    const showDomesticInfo = showBookingInformation && workflow.showDomesticForm;
    const showImportInfo = showBookingInformation && workflow.showImportForm;
    const showExportInfo = showBookingInformation && workflow.showExportForm;
    const label = (key, fallback) => labelText(t, key, fallback);
    const additionalContainerNumbers = Array.isArray(instructionData.additionalContainerNumbers) ? instructionData.additionalContainerNumbers : [];
    const setInstructionField = (name, value) => handleChange({ target: { name, value } });
    const setDomesticBookingInfoField = (name, value) => {
      setInstructionField("domesticBookingInfo", {
        ...domesticBookingInfo,
        [name]: value
      });
    };
    const setImportBookingInfoField = (name, value) => {
      setInstructionField("importBookingInfo", {
        ...importBookingInfo,
        [name]: value
      });
    };
    const setExportBookingInfoField = (name, value) => {
      setInstructionField("exportBookingInfo", {
        ...exportBookingInfo,
        [name]: value
      });
    };
    const setPickupDeliveryInfoField = (name, value) => {
      setInstructionField("pickupDeliveryInfo", {
        ...pickupDeliveryInfo,
        [name]: value
      });
    };
    const updateAdditionalContainerNumber = (index, value) => {
      const next = [...additionalContainerNumbers];
      next[index] = value;
      setInstructionField("additionalContainerNumbers", next);
    };
    return (
      <Grid item xs={12}>
        <span>
            <Typography component="h2" variant="h5" style={{marginTop:15, color:colors.BLACK, fontFamily:FONT_FAMILY}}>
                {t('delivery_information')}
            </Typography>
            {auth.profile.usertype === 'customer' && !auth.profile.firstName ?
            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                variant="outlined"
                margin="normal"
                required = {auth.profile.firstName ? false : true }
                fullWidth
                id="firstName"
                label={t('firstname')}
                name="firstName"
                autoComplete="firstName"
                onChange={handleChange}
                value={profileData.firstName}
                autoFocus
                className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
              />
            </Grid>
            : null }
            {auth.profile.usertype === 'customer' && !auth.profile.lastName ?
            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                variant="outlined"
                margin="normal"
                required = {auth.profile.lastName ? false : true }
                fullWidth
                id="lastName"
                label={t('lastname')}
                name="lastName"
                autoComplete="lastName"
                onChange={handleChange}
                value={profileData.lastName}
                className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
              />
            </Grid>
            : null }
            {auth.profile.usertype === 'customer' && !auth.profile.email ?
            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                variant="outlined"
                margin="normal"
                required = {auth.profile.email ? false : true }
                fullWidth
                id="email"
                label={t('email')}
                name="email"
                autoComplete="email"
                onChange={handleChange}
                value={profileData.email}
                className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
              />
            </Grid>
            : null }

            <OtherPerson
              classes = {classes}
              otherPerson={otherPerson}
              handleChange={handleChange}
              setOtherPerson={setOtherPerson}
              instructionData={instructionData}
            />

            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                variant="outlined"
                margin="normal"
                fullWidth
                id="pickUpInstructions"
                label={t('pickUpInstructions')}
                name="pickUpInstructions"
                autoComplete="pickUpInstructions"
                onChange={handleChange}
                value={instructionData.pickUpInstructions}
                className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                variant="outlined"
                margin="normal"
                fullWidth
                id="deliveryInstructions"
                label={t('deliveryInstructions')}
                name="deliveryInstructions"
                autoComplete="deliveryInstructions"
                onChange={handleChange}
                value={instructionData.deliveryInstructions}
                className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
              />
            </Grid>
            {showPrimaryInfo ?
              <>
                <Typography component="h3" variant="h6" style={{marginTop:15, color:colors.BLACK, fontFamily:FONT_FAMILY}}>
                    {label('primary_booking_information_form')}
                </Typography>
                {uniqueFields(showContainerInfo ? containerBookingTextFields : generalCargoTextFields).map((field) => (
                  <Grid item xs={12} key={field}>
                    <TextField
                      InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      type={field === "itemDescription" ? "text" : "number"}
                      id={field}
                      label={label(field)}
                      name={field}
                      autoComplete={field}
                      onChange={handleChange}
                      value={instructionData[field] || ""}
                      className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                      style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                    />
                  </Grid>
                ))}
                <Typography className={classes.typography} style={{ fontFamily: FONT_FAMILY, marginTop: 8 }}>
                  {label('cargoDimensions')}
                </Typography>
                <Grid container spacing={1}>
                  {cargoDimensionFields.map((field) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <TextField
                        InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        type="number"
                        id={field}
                        label={label(field)}
                        name={field}
                        autoComplete={field}
                        onChange={handleChange}
                        value={instructionData[field] || ""}
                        className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                        style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                      />
                    </Grid>
                  ))}
                </Grid>
                {availableServiceTypes.length > 1 ?
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal" variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                      <InputLabel id="serviceType-label" style={{ fontFamily: FONT_FAMILY }}>{label("serviceType")}</InputLabel>
                      <Select
                        labelId="serviceType-label"
                        id="serviceType"
                        name="serviceType"
                        value={normalizeServiceType(instructionData.serviceType)}
                        onChange={handleChange}
                        label={label("serviceType")}
                      >
                        {availableServiceTypes.map((option) => (
                          <MenuItem key={option} value={option}>{label(option)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                : null}
                <Grid item xs={12}>
                  <TextField
                    InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="note"
                    label={label("note")}
                    name="note"
                    multiline
                    minRows={2}
                    inputProps={{ maxLength: 70 }}
                    onChange={handleChange}
                    value={instructionData.note || ""}
                    className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                    style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                  />
                </Grid>
                {!showPickupDeliveryInfo ?
                  <Grid item xs={12}>
                    <TextField
                      InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      id="exactDeliveryAddress"
                      label={label("exactDeliveryAddress")}
                      name="exactDeliveryAddress"
                      onChange={handleChange}
                      value={instructionData.exactDeliveryAddress || ""}
                      className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                      style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                    />
                  </Grid>
                : null}
                {showPickupDeliveryInfo ?
                  <>
                    <Typography component="h3" variant="h6" style={{marginTop:15, color:colors.BLACK, fontFamily:FONT_FAMILY}}>
                      {label("pickup_delivery_information_form")}
                    </Typography>
                    {["pickupAddress", "deliveryAddress", "receiverName", "receiverContactNumber", "preferredPickupDate", "preferredPickupTime"].map((field) => (
                      <Grid item xs={12} sm={field === "pickupAddress" || field === "deliveryAddress" ? 12 : 6} key={field}>
                        <TextField
                          InputLabelProps={{ style: { fontFamily: FONT_FAMILY }, shrink: field === "preferredPickupDate" || field === "preferredPickupTime" }}
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          id={`pickupDelivery-${field}`}
                          label={label(field)}
                          value={pickupDeliveryInfo[field] || ""}
                          type={field === "preferredPickupDate" ? "date" : field === "preferredPickupTime" ? "time" : "text"}
                          onChange={(event) => setPickupDeliveryInfoField(field, event.target.value)}
                          className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                          style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                        />
                      </Grid>
                    ))}
                  </>
                : null}
                {!showPickupDeliveryInfo && showShipmentTypeSelector ?
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal" variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                      <InputLabel id="shipmentType-label" style={{ fontFamily: FONT_FAMILY }}>{label("shipmentType")}</InputLabel>
                      <Select
                        labelId="shipmentType-label"
                        id="shipmentType"
                        name="shipmentType"
                        value={instructionData.shipmentType || ""}
                        onChange={handleChange}
                        label={label("shipmentType")}
                      >
                        {shipmentTypeOptions.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                : null}
                {!showPickupDeliveryInfo && showDomesticShipmentSelector ?
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal" variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                      <InputLabel id="shipmentType-domestic-label" style={{ fontFamily: FONT_FAMILY }}>{label("shipmentType")}</InputLabel>
                      <Select
                        labelId="shipmentType-domestic-label"
                        id="shipmentType"
                        name="shipmentType"
                        value={instructionData.shipmentType || ""}
                        onChange={handleChange}
                        label={label("shipmentType")}
                      >
                        <MenuItem value="Domestic Shipment">Domestic Shipment</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                : null}
                  {!showPickupDeliveryInfo && showDomesticInfo ?
                    <>
                    <Typography component="h3" variant="h6" style={{marginTop:15, color:colors.BLACK, fontFamily:FONT_FAMILY}}>
                      {label("domestic_booking_information_form")}
                    </Typography>
                    <Grid item xs={12}>
                      <TextField
                        InputLabelProps={{ style: { fontFamily: FONT_FAMILY }, shrink: true }}
                        variant="outlined"
                        margin="normal"
                        fullWidth
                        type="date"
                        id="domesticBookingDate"
                        label={label("domesticBookingDate")}
                        value={domesticBookingInfo.bookingDate || ""}
                        onChange={(event) => setDomesticBookingInfoField("bookingDate", event.target.value)}
                        className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                        style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal" variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                        <InputLabel id="domesticShipmentType-label" style={{ fontFamily: FONT_FAMILY }}>{label("domesticShipmentType")}</InputLabel>
                        <Select
                          labelId="domesticShipmentType-label"
                          id="domesticShipmentType"
                          value={domesticBookingInfo.shipmentType || ""}
                          onChange={(event) => setDomesticBookingInfoField("shipmentType", event.target.value)}
                          label={label("domesticShipmentType")}
                        >
                          {domesticShipmentTypeOptions.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {domesticBookingTextFields.map((item) => (
                      <Grid item xs={12} key={item.field}>
                        <TextField
                          InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          id={`domestic-${item.field}`}
                          label={label(item.labelKey)}
                          value={domesticBookingInfo[item.field] || ""}
                          multiline={!!item.multiline}
                          minRows={item.multiline ? 2 : undefined}
                          onChange={(event) => setDomesticBookingInfoField(item.field, event.target.value)}
                          className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                          style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                        />
                      </Grid>
                      ))}
                    </>
                  : null}
                  {!showPickupDeliveryInfo && showImportInfo ?
                    <>
                      <Grid item xs={12}>
                        <Typography component="h3" variant="h6" style={{ marginTop: 8, color: colors.BLACK, fontFamily: FONT_FAMILY }}>
                          {label('import_booking_information_form', 'Import Booking Information Form')}
                        </Typography>
                      </Grid>
                      {importBookingTextFields.map((item) => (
                        <Grid item xs={12} sm={item.multiline ? 12 : 6} key={item.field}>
                          <TextField
                            InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                            variant="outlined"
                            fullWidth
                            id={`import-${item.field}`}
                            label={label(item.labelKey)}
                            value={importBookingInfo[item.field] || ""}
                            multiline={!!item.multiline}
                            minRows={item.multiline ? 2 : undefined}
                            onChange={(event) => setImportBookingInfoField(item.field, event.target.value)}
                            className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                            style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                          />
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                          <InputLabel id="import-containerType-label" style={{ fontFamily: FONT_FAMILY }}>{label('containerType')}</InputLabel>
                          <Select
                            labelId="import-containerType-label"
                            id="import-containerType"
                            value={importBookingInfo.containerType || ""}
                            onChange={(event) => setImportBookingInfoField("containerType", event.target.value)}
                            label={label('containerType')}
                          >
                            <MenuItem value=""><em>{label("select", "Select")}</em></MenuItem>
                            {containerTypeOptions.map((option) => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                          <InputLabel id="import-containerSize-label" style={{ fontFamily: FONT_FAMILY }}>{label('containerSize')}</InputLabel>
                          <Select
                            labelId="import-containerSize-label"
                            id="import-containerSize"
                            value={selectedCarContainerSize || importBookingInfo.containerSize || ""}
                            disabled
                            label={label('containerSize')}
                          >
                            <MenuItem value={selectedCarContainerSize || importBookingInfo.containerSize || ""}>{selectedCarContainerSize || importBookingInfo.containerSize || ""}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {logisticsDateFields.map((field) => (
                        <Grid item xs={12} sm={6} key={`import-${field}`}>
                          <TextField
                            InputLabelProps={{ shrink: true, style: { fontFamily: FONT_FAMILY } }}
                            variant="outlined"
                            fullWidth
                            type="datetime-local"
                            id={`import-${field}`}
                            label={label(field)}
                            value={importBookingInfo[field] || ""}
                            onChange={(event) => setImportBookingInfoField(field, event.target.value)}
                            className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                            style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                          />
                        </Grid>
                      ))}
                    </>
                  : null}
                  {!showPickupDeliveryInfo && showExportInfo ?
                    <>
                      <Grid item xs={12}>
                        <Typography component="h3" variant="h6" style={{ marginTop: 8, color: colors.BLACK, fontFamily: FONT_FAMILY }}>
                          {label('export_booking_information_form', 'Export Booking Information Form')}
                        </Typography>
                      </Grid>
                      {exportBookingTextFields.map((item) => (
                        <Grid item xs={12} sm={item.multiline ? 12 : 6} key={item.field}>
                          <TextField
                            InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                            variant="outlined"
                            fullWidth
                            id={`export-${item.field}`}
                            label={label(item.labelKey)}
                            value={exportBookingInfo[item.field] || ""}
                            multiline={!!item.multiline}
                            minRows={item.multiline ? 2 : undefined}
                            onChange={(event) => setExportBookingInfoField(item.field, event.target.value)}
                            className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                            style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                          />
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                          <InputLabel id="export-containerType-label" style={{ fontFamily: FONT_FAMILY }}>{label('containerType')}</InputLabel>
                          <Select
                            labelId="export-containerType-label"
                            id="export-containerType"
                            value={exportBookingInfo.containerType || ""}
                            onChange={(event) => setExportBookingInfoField("containerType", event.target.value)}
                            label={label('containerType')}
                          >
                            <MenuItem value=""><em>{label("select", "Select")}</em></MenuItem>
                            {containerTypeOptions.map((option) => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                          <InputLabel id="export-containerSize-label" style={{ fontFamily: FONT_FAMILY }}>{label('containerSize')}</InputLabel>
                          <Select
                            labelId="export-containerSize-label"
                            id="export-containerSize"
                            value={selectedCarContainerSize || exportBookingInfo.containerSize || ""}
                            disabled
                            label={label('containerSize')}
                          >
                            <MenuItem value={selectedCarContainerSize || exportBookingInfo.containerSize || ""}>{selectedCarContainerSize || exportBookingInfo.containerSize || ""}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {exportDateFields.map((field) => (
                        <Grid item xs={12} sm={6} key={`export-${field}`}>
                          <TextField
                            InputLabelProps={{ shrink: true, style: { fontFamily: FONT_FAMILY } }}
                            variant="outlined"
                            fullWidth
                            type="datetime-local"
                            id={`export-${field}`}
                            label={label(field)}
                            value={exportBookingInfo[field] || ""}
                            onChange={(event) => setExportBookingInfoField(field, event.target.value)}
                            className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                            style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                          />
                        </Grid>
                      ))}
                    </>
                  : null}
                </>
              : null}
              {false && !showPickupDeliveryInfo && showContainerInfo && !showImportInfo && !showExportInfo ?
                <>
            <Typography component="h3" variant="h6" style={{marginTop:15, color:colors.BLACK, fontFamily:FONT_FAMILY}}>
                {label('container_shipping_info')}
            </Typography>
            {uniqueFields(logisticsTextFields).map((field) => (
              <React.Fragment key={field}>
                <Grid item xs={12}>
                  <TextField
                    InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id={field}
                    label={label(field)}
                    name={field}
                    autoComplete={field}
                    onChange={handleChange}
                    value={instructionData[field] || ""}
                    className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                    style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                  />
                </Grid>
                {field === "containerNumber" ?
                  <>
                    {additionalContainerNumbers.map((value, index) => (
                      <Grid item xs={12} key={`additional-container-number-${index}`}>
                        <TextField
                          InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          id={`additionalContainerNumbers-${index}`}
                          label={`${label("additional_container_numbers")} ${index + 1}`}
                          value={value || ""}
                          onChange={(event) => updateAdditionalContainerNumber(index, event.target.value)}
                          className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                          style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        onClick={() => setInstructionField("additionalContainerNumbers", [...additionalContainerNumbers, ""])}
                        style={{ marginTop: 8, fontFamily: FONT_FAMILY }}
                      >
                        {label("add_additional_container_number")}
                      </Button>
                    </Grid>
                  </>
                : null}
              </React.Fragment>
            ))}
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                <InputLabel id="containerType-label" style={{ fontFamily: FONT_FAMILY }}>{label('containerType')}</InputLabel>
                <Select
                  labelId="containerType-label"
                  id="containerType"
                  name="containerType"
                  value={instructionData.containerType || ""}
                  onChange={handleChange}
                  label={label('containerType')}
                  style={{direction:isRTL==='rtl'?'rtl':'ltr', fontFamily: FONT_FAMILY}}
                >
                  <MenuItem value=""><em>{t('select')}</em></MenuItem>
                  {containerTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" variant="outlined" className={isRTL==='rtl'?classes.inputRtl:classes.textField}>
                <InputLabel id="containerSize-label" style={{ fontFamily: FONT_FAMILY }}>{label('containerSize')}</InputLabel>
                <Select
                  labelId="containerSize-label"
                  id="containerSize"
                  name="containerSize"
                  value={instructionData.containerSize || ""}
                  onChange={handleChange}
                  disabled={!!selectedCarContainerSize}
                  label={label('containerSize')}
                  style={{direction:isRTL==='rtl'?'rtl':'ltr', fontFamily: FONT_FAMILY}}
                >
                  <MenuItem value=""><em>{t('select')}</em></MenuItem>
                  <MenuItem value="10ft">10ft</MenuItem>
                  <MenuItem value="20ft">20ft</MenuItem>
                  <MenuItem value="40ft">40ft</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <ContainerYardFilteredSelector
                label={label('pickup_container_yard')}
                name="pickupContainerYardId"
                value={instructionData.pickupContainerYardId || ""}
                onChange={handleChange}
                containerYards={activeContainerYards}
                classes={classes}
                isRTL={isRTL}
                t={t}
              />
            </Grid>
            <Grid item xs={12}>
              <ContainerYardFilteredSelector
                label={label('empty_return_container_yard')}
                name="emptyReturnContainerYardId"
                value={instructionData.emptyReturnContainerYardId || ""}
                onChange={handleChange}
                containerYards={activeContainerYards}
                classes={classes}
                isRTL={isRTL}
                t={t}
                disableUnavailable
              />
            </Grid>
            {logisticsDateFields.map((field) => (
              <Grid item xs={12} key={field}>
                <TextField
                  InputLabelProps={{ shrink: true, style: { fontFamily: FONT_FAMILY } }}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  type="datetime-local"
                  id={field}
                  label={label(field)}
                  name={field}
                  onChange={handleChange}
                  value={instructionData[field] || ""}
                  className={isRTL==='rtl'?classes.inputRtl:classes.textField}
                  style={{direction:isRTL==='rtl'?'rtl':'ltr'}}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography className={classes.typography} style={{ fontFamily: FONT_FAMILY, marginTop: 8 }}>
                {label('date_confirmation_help')}
              </Typography>
              <Button
                variant={instructionData.dateConfirmationOk ? "contained" : "outlined"}
                color="primary"
                onClick={() => handleChange({ target: { name: "dateConfirmationOk", value: true } })}
                style={{ marginTop: 8, minWidth: 110, fontFamily: FONT_FAMILY }}
              >
                {instructionData.dateConfirmationOk ? label('date_confirmation_confirmed') : label('date_confirmation_ok_button')}
              </Button>
            </Grid>
              </>
            : null}
            {settings.bookingFlow === "0" && auth.profile.usertype === 'customer' ? 
              <Grid item xs={12}>
             <RadioGroup row name="bookingFlow" value={deliveryWithBid} onChange={handleChange}>
               <FormControlLabel key={"key0"} value={0} control={<Radio />} label={ <Typography className={classes.typography}>{t('system_price')}</Typography>} />
               <FormControlLabel key={"key1"} value={1} control={<Radio />} label={ <Typography className={classes.typography}>{t('bid')}</Typography>} />
             </RadioGroup>
           </Grid>
            :null}
        </span>
      </Grid>
    )
}

export const validateBookingObj = (t, bookingObject, instructionData, otherPerson = false, workflowConfigs = null) => {
    delete bookingObject.driverEstimates;
    const seededInstructionData = {
      ...instructionData,
      pickupDeliveryInfo: {
        ...normalizePickupDeliveryInfo(instructionData),
        pickupAddress: instructionData && instructionData.pickupDeliveryInfo && instructionData.pickupDeliveryInfo.pickupAddress
          ? instructionData.pickupDeliveryInfo.pickupAddress
          : (bookingObject && bookingObject.pickup && (bookingObject.pickup.description || bookingObject.pickup.add || "")),
        deliveryAddress: instructionData && instructionData.pickupDeliveryInfo && instructionData.pickupDeliveryInfo.deliveryAddress
          ? instructionData.pickupDeliveryInfo.deliveryAddress
          : (bookingObject && bookingObject.drop && (bookingObject.drop.description || bookingObject.drop.add || ""))
      }
    };
    const sanitizedInstructionData = sanitizeInstructionDataForVehicle(seededInstructionData, bookingObject.carDetails, workflowConfigs);
    const workflow = getBookingWorkflowForBooking(sanitizedInstructionData, bookingObject.carDetails, workflowConfigs);
    if (workflow.showPrimaryForm) {
      const primaryError = validatePrimaryBookingInformation(t, sanitizedInstructionData, bookingObject.carDetails, workflowConfigs);
      if (primaryError) {
        return { error: true, msg: primaryError };
      }
    }
    const domesticError = validateDomesticBookingInfo(t, sanitizedInstructionData, bookingObject.carDetails, workflowConfigs);
    if (domesticError) {
      return { error: true, msg: domesticError };
    }
    const importError = validateImportBookingInfo(t, sanitizedInstructionData, bookingObject.carDetails, workflowConfigs);
    if (importError) {
      return { error: true, msg: importError };
    }
    const exportError = validateExportBookingInfo(t, sanitizedInstructionData, bookingObject.carDetails, workflowConfigs);
    if (exportError) {
      return { error: true, msg: exportError };
    }
    const pickupDeliveryError = validatePickupDeliveryInfo(t, sanitizedInstructionData, bookingObject.carDetails, workflowConfigs);
    if (pickupDeliveryError) {
      return { error: true, msg: pickupDeliveryError };
    }
    bookingObject.instructionData = sanitizedInstructionData;
    return { bookingObject };
}

export const PanicSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { classes, data, handleTextChange } = props;
  return (
      <span>
          <Typography component="h1" variant="h5" style={{ marginTop: '15px', textAlign: isRTL === 'rtl' ? 'right' : 'left',fontFamily:FONT_FAMILY }}>
              {t('panic_num')}
          </Typography>
          <TextField
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              variant="outlined"
              margin="normal"
              fullWidth
              id="panic"
              label={t('panic_num')}
              className={isRTL === "rtl" ? [classes.rootRtl_1, classes.right] : classes.textField}
              name="panic"
              autoComplete="panic"
              onChange={handleTextChange}
              value={data.panic}
          />
      </span>
  )
}

export const DispatchSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { autoDispatch, onChange } = props;
  return (
      <FormControlLabel
          style={{ flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}
          control={
              <Switch
                  checked={autoDispatch}
                  onChange={onChange}
                  name="autoDispatch"
                  color="primary"
              />
          }
          label={ <Typography style={{fontFamily:FONT_FAMILY}}>{t('auto_dispatch')}</Typography>}
    />
  )
}

export const BookingImageSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { data, handleSwitchChange } = props;
  return (
      <span>
          <FormControlLabel
            style={{ marginTop: '10px', flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}
            control={
              <Switch
                checked={data.AllowDeliveryPickupImageCapture}
                onChange={handleSwitchChange}
                name="AllowDeliveryPickupImageCapture"
                color="primary"
              />
            }
            label={ <Typography style={{fontFamily:FONT_FAMILY}}>{t('allow_del_pkp_img')}</Typography>}
          />
          <FormControlLabel
            style={{ marginTop: '10px', flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}
            control={
              <Switch
                checked={data.AllowFinalDeliveryImageCapture}
                onChange={handleSwitchChange}
                name="AllowFinalDeliveryImageCapture"
                color="primary"
              />
            }
            label={ <Typography style={{fontFamily:FONT_FAMILY}}>{t('allow_del_final_img')}</Typography>}
          />
      </span>
  )
}

export const carTypeColumns = (t, isRTL, onClick) =>  [
  { title: t('name'), field: 'name',
  },
  { title: t('image'),  field: 'image',
    initialEditValue: 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png',
    render: rowData => rowData.image? <button onClick={()=>{onClick(rowData)}}><img alt='CarImage' src={rowData.image} style={{width: 50}}/></button>:null
  },
  { title: t('base_fare'), field: 'base_fare', type: 'numeric', 
   initialEditValue: 0 },
  { title: t('rate_per_unit_distance'), field: 'rate_per_unit_distance', type: 'numeric', 
   initialEditValue: 0},
  { title: t('rate_per_hour'), field: 'rate_per_hour', type: 'numeric', 
   initialEditValue: 0},
  { title: t('min_fare'), field: 'min_fare', type: 'numeric', 
  initialEditValue: 0},
  { title: t('convenience_fee'), field: 'convenience_fees', type: 'numeric', 
   initialEditValue: 0},
  {
    title: t('convenience_fee_type'),
    field: 'convenience_fee_type',
    lookup: { flat: t('flat'), percentage: t('percentage')},
  },
  {
    title: t('fleet_admin_comission'), field: 'fleet_admin_fee', type: 'numeric', 
   initialEditValue: 0
  },
  { title: t('extra_info'), field: 'extra_info' ,
},
  { title: t('position'), field: 'pos', type: 'numeric', defaultSort:'asc'}
];

export const acceptBid = (selectedBooking, selectedBidder) => {
  let bookingObj = {...selectedBooking};
  const selectedOffer = bookingObj.driverOffers ? bookingObj.driverOffers[selectedBidder] : null;
  if (!selectedOffer || selectedOffer.status === "cancelled") {
    return bookingObj;
  }
  const uid = selectedOffer.driver || selectedOffer.driverId || selectedBidder;
  const parsedAmount = parseFloat(selectedOffer.amount !== undefined ? selectedOffer.amount : (selectedOffer.trip_cost !== undefined ? selectedOffer.trip_cost : selectedOffer.bidPrice));
  const acceptedDriverOffer = {
    ...selectedOffer,
    driver: uid,
    driverId: uid,
    amount: isNaN(parsedAmount) ? 0 : parsedAmount,
    trip_cost: isNaN(parsedAmount) ? "0.00" : parsedAmount.toFixed(2),
    status: selectedOffer.status || "submitted"
  };
  bookingObj.acceptedOfferType = "driver";
  bookingObj.acceptedDriver = uid;
  bookingObj.acceptedDriverOffer = acceptedDriverOffer;
  bookingObj.selectedBid = acceptedDriverOffer;
  for(let key in (bookingObj.driverOffers || {})){
    if(key !== uid){
       delete  bookingObj.driverOffers[key];
    } else {
       bookingObj.driverOffers[key] = acceptedDriverOffer;
    }
  }
  for(let key in (bookingObj.requestedDrivers || {})){
      if(key !== uid){
        delete  bookingObj.requestedDrivers[key];
      }
  }
  return bookingObj;
}

export const getActiveFleetOffers = (booking) => {
  if (!booking || !booking.fleetOffers) {
    return [];
  }
  return Object.keys(booking.fleetOffers)
    .map((key) => ({ key, offer: booking.fleetOffers[key] }))
    .filter((item) => item.offer && item.offer.status !== "cancelled");
}

const hasFleetAdminValue = (value) => value !== undefined && value !== null && value !== "" && value !== false;

export const getActiveDriverOffers = (booking) => {
  if (!booking || !booking.driverOffers) {
    return [];
  }
  return Object.keys(booking.driverOffers)
    .map((key) => ({ key, offer: booking.driverOffers[key] }))
    .filter((item) => item.offer && item.offer.status !== "cancelled" && (!hasFleetAdminValue(item.offer.fleetadmin) || item.offer.legacyPublicBid === true));
}

export const getFleetOfferAmount = (offer) => (
  offer && offer.amount !== undefined ? offer.amount : (
    offer && offer.trip_cost !== undefined ? offer.trip_cost : 0
  )
);

export const getFleetAdminDisplayName = (userOrOffer) => {
  if (!userOrOffer) return "Logistics Company";
  const personName = [userOrOffer.firstName, userOrOffer.lastName].filter(Boolean).join(" ").trim();
  return (
    userOrOffer.companyName ||
    userOrOffer.company_name ||
    userOrOffer.businessName ||
    userOrOffer.business_name ||
    userOrOffer.fleetAdminName ||
    personName ||
    userOrOffer.email ||
    userOrOffer.mobile ||
    "Logistics Company"
  );
}

export const getFleetCompanyType = (userOrOffer) => {
  if (!userOrOffer) return "Logistics Company";
  return userOrOffer.companyType || userOrOffer.company_type || "Logistics Company";
}

export const getFleetOfferDisplayName = (offer) => {
  return getFleetAdminDisplayName(offer);
}

const getPreferredFleetAdminId = (booking) => (
  booking && (booking.preferredFleetAdminId || booking.favoriteTranspoId || booking.favoriteTranspoFleetAdmin || "")
);

const isPreferredFleetOffer = (booking, key, offer) => {
  const preferredFleetAdminId = getPreferredFleetAdminId(booking);
  return !!(
    preferredFleetAdminId &&
    (
      key === preferredFleetAdminId ||
      (offer && offer.fleetadmin === preferredFleetAdminId) ||
      (offer && offer.fleetAdminId === preferredFleetAdminId) ||
      (offer && offer.uid === preferredFleetAdminId)
    )
  );
}

export const BidModal = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { ref, role, selectedBooking, bidModalStatus,handleBidModalClose, classes, settings, acceptBid, acceptFleetBid, selectedBidder, handleChange } = props;
  const fleetOffers = getActiveFleetOffers(selectedBooking);
  const driverOffers = getActiveDriverOffers(selectedBooking);
  const canAcceptOffers = selectedBooking && selectedBooking.status === "NEW" && !selectedBooking.acceptedOfferType;
  return selectedBooking && role==='customer' && (fleetOffers.length > 0 || driverOffers.length > 0 || selectedBooking.acceptedOfferType)?
  <Modal
       disablePortal
       disableEnforceFocus
       disableAutoFocus
       open={bidModalStatus}
       onClose={handleBidModalClose}
       className={classes.modal}
       container={() => ref}
  >
      <Grid container spacing={2} className={classes.paper}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Typography variant="h6" style={{textAlign:isRTL=== 'rtl' ?'right':'left',fontFamily:FONT_FAMILY}}>{t('selectBid')}</Typography>
            {selectedBooking.acceptedOfferType ?
              <Typography style={{marginTop: 10, fontFamily: FONT_FAMILY}}>
                {labelText(t, "accepted_offer", "Accepted Offer")}: {selectedBooking.acceptedOfferType === "fleet" ? labelText(t, "transport_company_offer", "Transport Company Offer") : labelText(t, "driver_offer", "Driver Offer")}
              </Typography>
            : null}
          </Grid>
          {fleetOffers.length > 0 ?
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Typography variant="subtitle1" style={{fontFamily:FONT_FAMILY, fontWeight: 600}}>{labelText(t, "transport_company_offers", "Transport Company Offers")}</Typography>
              {fleetOffers.map(({ key, offer }) => {
                const preferredOffer = isPreferredFleetOffer(selectedBooking, key, offer);
                return (
                <Grid key={`fleet-${key}`} container spacing={1} style={{marginTop: 8, padding: 10, border: `1px solid ${preferredOffer ? colors.Action_Button_Back : colors.BORDER_BACKGROUND}`, borderRadius: 4, backgroundColor: preferredOffer ? "rgba(255, 193, 7, 0.12)" : "transparent"}}>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" style={{fontFamily: FONT_FAMILY, fontWeight: 600}}>{labelText(t, "transport_company_offer", "Transport Company Offer")}</Typography>
                    {preferredOffer ?
                      <Typography variant="caption" style={{display: "inline-block", marginLeft: 8, padding: "2px 8px", borderRadius: 12, backgroundColor: colors.Action_Button_Back, color: colors.Header_Background, fontFamily: FONT_FAMILY, fontWeight: 700}}>
                        {labelText(t, "favorite_transpo", "Favorite Transpo")} / {labelText(t, "preferred_by_customer", "Preferred by Customer")}
                      </Typography>
                    : null}
                    <Typography style={{fontFamily: FONT_FAMILY, fontWeight: 600}}>{getFleetOfferDisplayName(offer)}</Typography>
                    <Typography variant="body2" style={{fontFamily: FONT_FAMILY}}>{getFleetCompanyType(offer)}</Typography>
                    {offer.note ?
                      <Typography variant="body2" style={{fontFamily: FONT_FAMILY}}>{offer.note}</Typography>
                    : null}
                    {offer.updatedAt || offer.createdAt ?
                      <Typography variant="caption" style={{fontFamily: FONT_FAMILY}}>{moment(offer.updatedAt || offer.createdAt).format("lll")}</Typography>
                    : null}
                  </Grid>
                  <Grid item xs={12} sm={4} style={{textAlign:isRTL=== 'rtl' ?'left':'right'}}>
                    <Typography style={{fontFamily: FONT_FAMILY, fontWeight: 600}}>{formatCurrency(getFleetOfferAmount(offer), settings)}</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!canAcceptOffers}
                      style={{marginTop: 8, fontFamily: FONT_FAMILY}}
                      onClick={() => acceptFleetBid(key)}
                    >
                      {labelText(t, "accept_fleet_offer", "Accept Fleet Offer")}
                    </Button>
                  </Grid>
                </Grid>
                );
              })}
            </Grid>
          : null}
          {driverOffers.length > 0 ?
          <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControl component="fieldset">
              <FormLabel component="legend" style={{textAlign:isRTL=== 'rtl' ?'right':'left',fontFamily:FONT_FAMILY}}>{labelText(t, "driver_offers", "Driver Offers")}</FormLabel>
              <RadioGroup name="selectedBidder" value={selectedBidder} onChange={handleChange}>
              {driverOffers.map(({ key, offer }) => 
                  <FormControlLabel 
                  key={key} value={key} control={<Radio />} 
                  label={
                    <Typography className={classes.typography}>
                      {(offer.driver_name || offer.driverName || labelText(t, "driver_offer", "Driver Offer")) + " - " + formatCurrency(offer.trip_cost || offer.amount || offer.bidPrice, settings)}
                      {offer.note ? ` - ${offer.note}` : ""}
                    </Typography>
                  }
                  />  
              )}
              </RadioGroup>
          </FormControl>
          </Grid>
          : null}
          <Grid item xs={12} sm={12} md={12} lg={12}  style={{direction: isRTL=== 'rtl' ?'rtl' : 'ltr'}}>
          <Button onClick={handleBidModalClose} variant="contained" color="primary" style={{fontFamily:FONT_FAMILY}}>
          {t('cancel')}
          </Button>
          {driverOffers.length > 0 ?
          <Button variant="contained" color="primary" type="submit" disabled={!canAcceptOffers || !selectedBidder} style={{...(isRTL === 'rtl' ? {marginRight: 10} : {marginLeft: 10}), fontFamily: FONT_FAMILY }} onClick={acceptBid}>
           {t('selectBid')}
          </Button>
          : null}
          </Grid>
      </Grid>
  </Modal>
   :null
}

export const  downloadCsv = (data, fileName) => {
  const finalFileName = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
  a.setAttribute("download", finalFileName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export const DeliveryFlow = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { data , handleChange } = props;
  return(
  <span>
    <Typography component="h1" variant="h5" style={{ marginTop: '15px', textAlign: isRTL === 'rtl' ? 'right' : 'left',fontFamily:FONT_FAMILY }}>
        {t('booking_type')}
    </Typography>

    <Grid item xs={12} sm={12} md={12} lg={12} style={{textAlign:isRTL==='rtl'?'right':'left'}}>
      <FormControl component="fieldset">
      <RadioGroup  row name="booking_type" value={data} onChange={handleChange}>
          <FormControlLabel key={"key0"} value={0} control={<Radio />} label={ <Typography fontFamily={FONT_FAMILY}>{t('system_price_with_bid')}</Typography>}/>
          <FormControlLabel key={"key1"} value={1} control={<Radio />} label={ <Typography fontFamily={FONT_FAMILY}>{t('system_price')}</Typography>} />
          <FormControlLabel key={"key2"} value={2} control={<Radio />} label={ <Typography fontFamily={FONT_FAMILY}>{t('bid')}</Typography>} />
        </RadioGroup>
      </FormControl>
    </Grid>
  </span>
  )
} 

export const CustomerBidSettings = (props) => {
  return null;
}
